/**
 * mdx-stem-separator.js — ConvTDFNet (MDX-Net) browser inference, parameterized
 * per model. Sibling of stem-separator.js (CascadedNet 2019); shares ORT loading,
 * owns its own STFT/ISTFT + chunking + model URL PER ENGINE.
 *
 * 2026-07-22: refactored from a single hard-coded Kim engine into `makeEngine(cfg)`
 * so a SECOND model (UVR_MDXNET_KARA_2, lead/backing karaoke split) can run with
 * its own params WITHOUT copying 600 lines of DSP (the "diverged copies = bugs"
 * trap). `window.MdxStemSeparator` stays the Kim default instance — byte-identical
 * behaviour for /vocal-remover, /pitch, /ai-detector — and `window.MdxKaraokeSeparator`
 * is a second instance. Shared, param-free DSP (radix-2 FFT, Hann, reflect-pad,
 * resample) lives at module scope; nFft-dependent state (Bluestein tables, window,
 * session) lives per engine.
 *
 * Model contract (Kim_Vocal_2, primary:'vocal'):
 *   n_fft 7680  hop 1024  dim_f 3072  dim_t 256  compensate 1.009
 *
 *   ⚠️⚠️ n_fft здесь стоял 6144 с первого порта (2026-05-28) до 2026-08-22, и
 *   это была не «настройка», а НЕВЕРНЫЙ параметр модели: реестр UVR для
 *   Kim_Vocal_2 задаёт mdx_n_fft_scale_set=7680 (у KARA_2 рядом — 5120 при
 *   dim_f 2048, и там мы совпадали). Модель берёт первые dim_f=3072 бинов
 *   спектра, поэтому n_fft решает, КАКИЕ частоты она видит: при 6144 в 3072
 *   бина укладывается вся полоса до 22 кГц, при 7680 — до 17.6 кГц, то есть
 *   ровно та сетка, на которой её учили. Ошибка не ломала звук — она молча
 *   стоила качества.
 *   ЗАМЕР 2026-08-22 (пары на одних и тех же клипах MUSDB18-test):
 *     в браузере, 8 клипов: вокал 9.99 → 11.02, минусовка 14.91 → 15.31 SI-SDR,
 *       лучше на 8 из 8 клипов ПО ОБЕИМ дорожкам;
 *     в python на том же чекпойнте, 20 клипов: 9.82 → 10.25 и 12.51 → 13.22.
 *   Цена — около +8% времени (таблицы Bluestein те же: и 2·6144−1, и 2·7680−1
 *   округляются вверх до M=16384; растёт только длина чирп-умножения).
 *   input/output [1, 4, 3072, 256] ([L_re,L_im,R_re,R_im], dim_f, dim_t)
 *   output = predicted VOCALS spectrogram (NOT a mask); instrumental = mix − pred.
 *   (Was shipped inverted 05-28..07-23 by 705faba67 — see the polarity note in
 *   split(); measured voiceband 0.946 on prod bytes proves pred=vocals.)
 * Model contract (UVR_MDXNET_KARA_2, is_karaoke, primary:'instrumental'):
 *   n_fft 5120  hop 1024  dim_f 2048  dim_t 256  compensate 1.065
 *   output primary = instrumental + BACKING vocals; residual (mix − pred·comp) = LEAD
 *
 * Public API (per engine):
 *   preload(slug, onProgressOrOpts)  → Promise<onnxSession>
 *   split(audioBuffer, opts)         → Promise<{instrumental, vocal}>
 *   isWarm() → bool          size() → bytes loaded
 * opts: { onProgress: fn(0..1, stage, info), signal, slug, lane, gpuRetry, quiet }
 *   quiet — no per-call <label>_split_ok event (a caller walking a recording in
 *   windows reports the walk once); the measured rate is still remembered.
 *   stage — код этапа, не текст: 'loading-model' | 'inference' (те же, что у
 *   StemSeparator); info.phase — 'download' | 'compute'; у 'inference' ещё
 *   chunks / total / elapsedMs / etaSec / rateRealtime.
 *
 * split() returns {instrumental, vocal}; cfg.primary names the stem the net
 * predicts (pred·compensate), the other is the residual (mix − pred·compensate):
 *   Kim  (primary 'vocal'):        vocal = pred,          instrumental = residual
 *   KARA (primary 'instrumental'): instrumental = pred,   vocal (lead) = residual
 */
(function(){
'use strict';

// ── One file, two places ────────────────────────────────────────────────────
// A page loads this file with a <script>. On the WORKER lane (makeEngine →
// splitInWorker) the same file is the worker's own script: the model, the STFT
// and the chunk loop then live in a thread the page can TERMINATE. That is the
// only way a hung WebGPU run is ever stopped, and the only way the memory this
// engine takes is ever given back. Measured 13.10.2026 on a real 121 s clip
// (working set of the whole browser tree, tests/playwright/lib/browser-rss.mjs):
// after the split and free() the tab still held +930 MB on WebGPU and +1138 MB
// on wasm — main-thread wasm memory and ORT's proxy worker never shrink.
// A worker has no `window`; under this one name every line below reads the same
// in both places (on a page `self` IS the window).
var IN_WORKER = (typeof WorkerGlobalScope !== 'undefined' && typeof self !== 'undefined' && self instanceof WorkerGlobalScope);
var window = (typeof self !== 'undefined' && self) || (typeof globalThis !== 'undefined' && globalThis) || {};
// Worker lane only — what the page resolved for this worker: it cannot see
// window.__ortBuild, the model <meta> tags or the page's storage.
var _wk = null;

var SR = 44100;

// ── Shared ORT loading (one runtime, many engines) ──
// WHICH build is not decided here: window.__ortBuild (device-capabilities.js)
// owns it. An iPhone gets the plain WASM build — JSEP + WebGPU there is what
// «the first chunk hung forever» (18.08) and «Страница вылетает» (22.09, #7527)
// were, and Kim loaded on iPhone WebGPU 116 times for 9 finished splits. A Mac
// or iPad keeps it (ORT_OPTS below). The literal is only for a page that has
// no owner (OrtBuildSingleOwnerTest).
// Both separators ask for the SAME answer (they share one window.ort): keep the
// JSEP build + WebGPU on desktop-class WebKit, where production measured it
// working and 3–20× faster than wasm (Mac/iPad Safari 26/27); iPhone gets the
// plain wasm build (see __ortBuild.jsepOnDesktopWebKit for the numbers).
var ORT_OPTS = { jsepOnDesktopWebKit: true };
function ortBuild(){ return _wk ? _wk.build : (window.__ortBuild || null); }
// The owner's answers, carried to the worker as plain values and given back the
// owner's shape — every accessor below then has one form for both places.
function ortBuildFrom(o){
    return {
        url: function(){ return o.primary; },
        fallback: function(){ return o.fallback || null; },
        wasmBase: function(){ return o.wasmBase; },
        wasmOnly: function(){ return !!o.wasmOnly; },
        legacy: function(){ return o.legacy ? { url: o.primary, fallbackUrl: o.fallback || null, wasmBase: o.wasmBase, simd: true } : null; },
        initFailed: function(){ return false; }   // the page decides the step-down (splitInWorker)
    };
}
// ORT 1.18 — the lane of a browser that cannot RESERVE the shared 4 GiB memory the
// current build imports (WebKit before 18: «no available backend found. ERR: [wasm]
// RangeError: Out of memory» at the first session). The owner learns it from the
// first failure (noShareStepDown below tells it) and answers the legacy build from
// then on. Only with SIMD: a CPU without it keeps its own answer in this file.
function ortLegacy(){
    var b = ortBuild(), L = (b && typeof b.legacy === 'function') ? b.legacy() : null;
    return (L && L.simd) ? L : null;
}
function ortPrimary(){ var L = ortLegacy(); if (L) return L.url; var b = ortBuild(); return b ? b.url(ORT_OPTS) : '/vendor/onnxruntime/ort.min.js'; }
function ortFallback(){ var L = ortLegacy(); if (L) return L.fallbackUrl; var b = ortBuild(); return (b && typeof b.fallback === 'function') ? b.fallback(ORT_OPTS) : null; }
function ortWasmBase(){ var L = ortLegacy(); if (L) return L.wasmBase; var b = ortBuild(); return (b && typeof b.wasmBase === 'function') ? b.wasmBase() : '/vendor/onnxruntime/'; }
function ortWasmOnly(){ if (ortLegacy()) return true; var b = ortBuild(); return !!(b && typeof b.wasmOnly === 'function' && b.wasmOnly(ORT_OPTS)); }
// Was that failure the reservation one, and is there a build that needs none? Then
// the runtime that failed is dropped (a failed init is cached by it; the generation
// bump tells the other separator, which shares window.ort), and 1.18 is loaded in its
// place — after a pause: an immediate retry meets the same address space.
// Resolves true when the caller should create its session once more, on ['wasm'].
// «Is the runtime on the page the legacy one» is read off the runtime itself:
// window.ort is shared, and the other separator may be the one that loaded it.
function ortOnLegacy(){
    try { var v = window.ort && ort.env && ort.env.versions && (ort.env.versions.web || ort.env.versions.common); return /^1\.18\./.test(String(v || '')); }
    catch(_){ return false; }
}
async function noShareStepDown(slug, err, from){
    var b = ortBuild();
    if (!(b && typeof b.initFailed === 'function')) return false;
    if (!b.initFailed(err, { legacy: ortOnLegacy() ? 'legacy' : '', from: from })) return false;
    window.__ortGen = ortGen() + 1;
    try { delete window.ort; } catch(_){ try { window.ort = undefined; } catch(__){} }
    ortLoadPromise = null;
    await new Promise(function(r){ setTimeout(r, 1000); });
    try { await loadOrtScript(); } catch(_){ return false; }
    reportTelemetry(slug, 'ort_noshare_stepdown', { from: from, legacy: ortOnLegacy() ? 1 : 0 });
    return ortOnLegacy();
}
var ortLoadPromise = null;
var ortLoadGen = -1;   // window.__ortGen the load promise belongs to

// ⚠️⚠️ Ключ замеренного темпа ВЕРСИОНИРОВАН, и это не украшение. Значение под
// ним — сколько РЕАЛЬНОГО времени занимает секунда звука на ЭТОЙ машине, и
// именно оно решает, включать ли Ультру по умолчанию (`defaultEngineVerdict`:
// >2.5 — нет). 2026-08-22 движок ускорен в 2–3 раза, то есть КАЖДОЕ ранее
// записанное значение стало завышенным во столько же раз: не поменяй мы ключ,
// вернувшийся пользователь, чьё устройство когда-то намерило 3.0, так и остался
// бы без Ультры, хотя теперь она у него идёт за 1.2. Починка, до которой не
// доезжает половина аудитории из-за кэша прежнего мира, — не починка.
// Меняешь скорость движка ощутимо — подними RATE_KEY.
var RATE_KEY = 'mdx_rate2_';
// A measured rate belongs to the BUILD it was measured on. An iPhone that once
// measured «fast» on JSEP + WebGPU now runs the plain wasm build (8–14× slower
// and ~1.4 GB for one chunk in WebKit, measured 24.09.2026) — reading the old
// number would put Kim on the whole song and kill the tab. Devices whose build
// did not change (Mac/iPad with WebGPU, Android, desktops) keep their key.
function rateKey(label){ return RATE_KEY + label + (ortWasmOnly() ? '_wasm' : ''); }

function reportTelemetry(slug, action, payload){
    // Worker lane: the page owns telemetry — the event travels as a message.
    if (_wk) { try { _wk.post({ type: 'tele', slug: slug || '', action: action, payload: payload || {} }); } catch(_){} return; }
    try {
        if (typeof window.__ae === 'function') {
            window.__ae('feature_use', Object.assign({ slug: slug || 'mdx-stem-separator', action: action }, payload || {}));
        }
    } catch(_){}
}

// ── Shared ORT-runtime health (generation model) ──
// ONE wasm instance serves every ORT consumer on the page: both MDX engines
// here plus stem-separator.js (CascadedNet). A WebAssembly TRAP ("memory
// access out of bounds", "unreachable") leaves that instance CORRUPT — the
// allocator dies mid-operation, so every later run on it is doomed no matter
// which engine calls (Sentry TIMBRICA-FRONTEND-CE: one trap cascaded into
// «все варианты не удались»). Pretending the runtime is still usable is the
// bug; the clean recovery — same move stem-separator's resetOrtAfterPoison
// established for init-poison — is to DISCARD the whole runtime: bump the
// generation, drop window.ort, and let the next consumer re-init a fresh
// instance. Engines stamp the generation their session was created under and
// lazily drop sessions that belong to a dead generation (see makeEngine).
function ortGen(){ return window.__ortGen | 0; }
function isWasmTrap(e){
    if (!e) return false;
    if (typeof WebAssembly !== 'undefined' && WebAssembly.RuntimeError && e instanceof WebAssembly.RuntimeError) return true;
    var m = String((e && e.message) || e);
    return /memory access out of bounds|unreachable(?: executed)?|table index is out of bounds|index out of bounds|call stack exhausted/i.test(m);
}
function ortTrapReset(slug, where){
    window.__ortGen = ortGen() + 1;
    try { delete window.ort; } catch(_){ try { window.ort = undefined; } catch(__){} }
    ortLoadPromise = null;
    reportTelemetry(slug, 'ort_runtime_trap_reset', { where: where, gen: ortGen() });
}

// Worker lane: the runtime arrives by importScripts — the same primary, the same
// CDN twin, the same wasm directory and thread rule as on the page below.
function loadOrtInWorker(){
    return new Promise(function(resolve, reject){
        var ok = false, fb = ortFallback();
        try { importScripts(ortPrimary()); ok = !!self.ort; } catch(_){}
        if (!ok && fb) { try { importScripts(fb); ok = !!self.ort; } catch(_){} }
        if (!ok) { ortLoadPromise = null; reject(new Error('ORT load failed (worker lane)')); return; }
        try {
            var wp = ortWasmBase();
            if (wp.charAt(0) === '/' && typeof location !== 'undefined' && location.origin) wp = location.origin + wp;
            ort.env.wasm.wasmPaths = wp;
            ort.env.wasm.numThreads = (!ortLegacy() && typeof crossOriginIsolated !== 'undefined' && crossOriginIsolated)
                ? Math.min(navigator.hardwareConcurrency || 4, 4) : 1;
        } catch(_){}
        resolve('loaded');
    });
}
// A lost GPU device answers nothing, ever: the run in flight simply never
// settles. The worker says so the moment the browser does, instead of leaving
// the page to learn it from a minute of silence.
function watchGpuLoss(){
    try {
        Promise.resolve(ort.env && ort.env.webgpu && ort.env.webgpu.device).then(function(dev){
            if (!dev || !dev.lost || typeof dev.lost.then !== 'function') return;
            dev.lost.then(function(info){
                try { _wk.post({ type: 'gpu_lost', reason: String((info && info.reason) || ''), message: String((info && info.message) || '').slice(0, 120) }); } catch(_){}
            });
        }, function(){});
    } catch(_){}
}

// The PAGE lane's half (15.10.2026): the same loss, answered the same way. Before, the page did
// not listen — a lost device left session.run unsettled until the chunk watchdog (60 s after the
// first chunk, 120 s on it; measured on the live page: rejected 60.3 s after device.destroy()), and
// the timeout then wrote the device a «slow» pace although it was not slow.
// → { gen, promise } for the current runtime's device; the promise settles when it is lost.
var _pageGpuLost = null;
function watchPageGpuLoss(){
    var never = new Promise(function(){});
    try {
        var p = Promise.resolve(ort.env && ort.env.webgpu && ort.env.webgpu.device).then(function(dev){
            return (dev && dev.lost && typeof dev.lost.then === 'function') ? dev.lost : never;
        }, function(){ return never; });
        _pageGpuLost = { gen: ortGen(), promise: p };
    } catch(_){ _pageGpuLost = null; }
}
// A watch from a runtime that has since been dropped (ortTrapReset) says nothing about this one.
function pageGpuLostSignal(){
    return (_pageGpuLost && _pageGpuLost.gen === ortGen()) ? _pageGpuLost.promise : null;
}

function loadOrtScript(){
    if (window.ort) return Promise.resolve('cached');
    // A promise from a DEAD generation is not a runtime: when another module
    // drops window.ort (ortTrapReset, or a page handing the runtime to another
    // engine), this module's promise is still the resolved one from before —
    // returning it handed callers `ort === undefined` and «Cannot read
    // properties of undefined (reading 'InferenceSession')» (reproduced
    // 24.09.2026: Kim on /profanity-remover, then CascadedNet). The generation
    // decides whether the promise still means anything.
    if (ortLoadPromise && ortLoadGen !== ortGen()) ortLoadPromise = null;
    if (ortLoadPromise) return ortLoadPromise;
    ortLoadGen = ortGen();
    if (IN_WORKER) { ortLoadPromise = loadOrtInWorker(); return ortLoadPromise; }
    ortLoadPromise = new Promise(function(resolve, reject){
        function tryLoad(url, onFail){
            var s = document.createElement('script');
            s.src = url;
            s.onload = function(){
                try {
                    if (window.ort && ort.env && ort.env.wasm) {
                        var _wp = ortWasmBase();
                        if (_wp.charAt(0) === '/' && typeof location !== 'undefined' && location.origin) _wp = location.origin + _wp;
                        ort.env.wasm.wasmPaths = _wp;
                        // the legacy dir has no threaded wasm
                        ort.env.wasm.numThreads = (!onLegacy && typeof crossOriginIsolated !== 'undefined' && crossOriginIsolated)
                            ? Math.min(navigator.hardwareConcurrency || 4, 4) : 1;
                    }
                } catch(_){}
                resolve('loaded');
            };
            s.onerror = onFail;
            document.head.appendChild(s);
        }
        var fb = ortFallback(), onLegacy = !!ortLegacy();
        tryLoad(ortPrimary(), function(){
            if (!fb) { ortLoadPromise = null; reject(new Error('ORT load failed (primary; no fallback owner)')); return; }
            tryLoad(fb, function(){
                ortLoadPromise = null;
                reject(new Error('ORT load failed (both sources)'));
            });
        });
    });
    return ortLoadPromise;
}

async function fetchWithRetry(url, attempts, backoffMs, signal, headers){
    var lastErr = null;
    var fetchSignal = (typeof AbortSignal !== 'undefined' && signal instanceof AbortSignal) ? signal : undefined;
    for (var a = 0; a < attempts; a++) {
        if (signal && signal.aborted) throw new DOMException('Aborted', 'AbortError');
        try {
            var r = await fetch(url, { credentials: 'omit', cache: a === 0 ? 'default' : 'reload', signal: fetchSignal, headers: headers || {} });
            if (!r.ok) throw new Error('HTTP ' + r.status);
            return r;
        } catch (e) {
            if (e && (e.name === 'AbortError' || (signal && signal.aborted))) throw e;
            lastErr = e;
            if (a < attempts - 1) {
                await new Promise(function(res){ setTimeout(res, backoffMs * Math.pow(2, a)); });
            }
        }
    }
    throw lastErr;
}

// Streaming download with mid-body Range resume (see original rationale: a mid-body
// blip on the 50-67 MB model must not throw the whole transfer away — the edge
// serves 206, so resume from offset).
var MAX_RESUMES = 4;
async function downloadWithResume(url, slug, onProgress, signal){
    var chunks = [], loaded = 0, expectedTotal = 0, resumes = 0;
    while (true) {
        if (signal && signal.aborted) throw new DOMException('Aborted', 'AbortError');
        var headers = loaded > 0 ? { 'Range': 'bytes=' + loaded + '-' } : null;
        var r = await fetchWithRetry(url, 3, 500, signal, headers);
        if (loaded > 0 && r.status !== 206) { chunks = []; loaded = 0; }
        if (!expectedTotal) {
            if (r.status === 206) {
                var cr = r.headers.get('Content-Range');
                var m = cr && cr.match(/\/(\d+)\s*$/);
                if (m) expectedTotal = parseInt(m[1], 10);
            } else {
                expectedTotal = parseInt(r.headers.get('Content-Length'), 10) || 0;
            }
        }
        var loadedAtAttemptStart = loaded;
        try {
            var reader = r.body.getReader();
            while (true) {
                if (signal && signal.aborted) { try { reader.cancel(); } catch(_){} throw new DOMException('Aborted', 'AbortError'); }
                var read = await reader.read();
                if (read.done) break;
                chunks.push(read.value);
                loaded += read.value.length;
                if (onProgress && expectedTotal) onProgress(Math.min(1, loaded / expectedTotal));
            }
            if (expectedTotal && loaded < expectedTotal) throw new Error('short body ' + loaded + '/' + expectedTotal);
            break;
        } catch (e) {
            if (e && (e.name === 'AbortError' || (signal && signal.aborted))) throw e;
            /* Лимит означает «подряд без единого байта», а не «всего обрывов»:
               иначе четыре обрыва убивают загрузку, даже если каждая попытка
               приносила мегабайты (дефект, стоивший 14 сессий на CLAP). */
            if (loaded - loadedAtAttemptStart >= 256 * 1024) resumes = 0;   // сброс только за СОДЕРЖАТЕЛЬНЫЙ прогресс
            resumes++;
            if (resumes > MAX_RESUMES) throw e;
            await new Promise(function(res){ setTimeout(res, 400 * resumes); });
        }
    }
    if (resumes > 0) reportTelemetry(slug, 'mdx_dl_resumed', { resumes: resumes, sizeMB: Math.round(loaded / 1048576) });
    var bytes = new Uint8Array(loaded);
    var off = 0;
    for (var c = 0; c < chunks.length; c++) { bytes.set(chunks[c], off); off += chunks[c].length; }
    return bytes;
}

// ORT-web 1.24 ships ONLY SIMD wasm — probe before a wasted 50-67 MB download.
var _simdSupport = null;
function wasmSimdSupported(){
    if (_simdSupport !== null) return _simdSupport;
    try {
        _simdSupport = WebAssembly.validate(new Uint8Array([
            0,97,115,109,1,0,0,0,1,5,1,96,0,1,123,3,2,1,0,10,10,1,8,0,65,0,253,15,253,98,11
        ]));
    } catch (_) { _simdSupport = false; }
    return _simdSupport;
}
/* The refusal for a CPU without SIMD is TYPED (2026-10-08): a plain Error read
   as «something broke» — /pitch would tell such a person to reload the page,
   and its fire-and-forget preload turned the rejection into an unhandled one
   (Sentry TIMBRICA-FRONTEND-P2, Yandex 24.10 on a SIMD-less Windows PC). code +
   expected follow the project contract for SIMD_UNSUPPORTED (ffmpeg-processor,
   engine-warmer, sentry-init's expected-error sink); userMessage is the honest,
   localised explanation from DeviceCapabilityGate — the one dictionary the
   uploader banner uses — so a caller can say WHY instead of «try again». */
function simdRefusal(slug){
    var e = new Error('WebAssembly SIMD is not supported by this browser');
    e.code = 'SIMD_UNSUPPORTED';
    e.expected = true;
    try {
        var G = window.DeviceCapabilityGate;
        var g = (G && typeof G.requireSimd === 'function') ? G.requireSimd(slug || 'mdx-stem-separator', { silent: true }) : null;
        if (g && !g.ok && g.message) e.userMessage = g.message;
    } catch (_) {}
    return e;
}

// ── Shared param-free DSP ──
function fftInPlace(re, im){
    var n = re.length;
    for (var i = 1, j = 0; i < n; i++) {
        var bit = n >> 1;
        for (; j & bit; bit >>= 1) j ^= bit;
        j ^= bit;
        if (i < j) { var t = re[i]; re[i] = re[j]; re[j] = t; t = im[i]; im[i] = im[j]; im[j] = t; }
    }
    for (var size = 2; size <= n; size <<= 1) {
        var half = size >> 1;
        var ang = -2 * Math.PI / size;
        var wR = Math.cos(ang), wI = Math.sin(ang);
        for (var i2 = 0; i2 < n; i2 += size) {
            var cR = 1, cI = 0;
            for (var j2 = 0; j2 < half; j2++) {
                var uR = re[i2+j2], uI = im[i2+j2];
                var vR = re[i2+j2+half]*cR - im[i2+j2+half]*cI;
                var vI = re[i2+j2+half]*cI + im[i2+j2+half]*cR;
                re[i2+j2] = uR+vR; im[i2+j2] = uI+vI;
                re[i2+j2+half] = uR-vR; im[i2+j2+half] = uI-vI;
                var nR = cR*wR - cI*wI; cI = cR*wI + cI*wR; cR = nR;
            }
        }
    }
}
function ifftInPlace(re, im){
    var n = re.length;
    for (var i = 0; i < n; i++) im[i] = -im[i];
    fftInPlace(re, im);
    for (var i = 0; i < n; i++) { re[i] /= n; im[i] = -im[i] / n; }
}
function hannPeriodic(N){
    var w = new Float32Array(N);
    for (var i = 0; i < N; i++) w[i] = 0.5 * (1 - Math.cos(2 * Math.PI * i / N));
    return w;
}
function reflectPad(src, pad){
    var n = src.length;
    var out = new Float32Array(n + 2 * pad);
    out.set(src, pad);
    for (var i = 0; i < pad; i++) out[pad - 1 - i] = src[i + 1];
    for (var i2 = 0; i2 < pad; i2++) out[pad + n + i2] = src[n - 2 - i2];
    return out;
}
async function ensure44100Stereo(audioBuffer){
    var src_L = audioBuffer.getChannelData(0);
    var src_R = audioBuffer.numberOfChannels > 1 ? audioBuffer.getChannelData(1) : src_L;
    if (audioBuffer.sampleRate === SR) return { L: src_L, R: src_R, length: audioBuffer.length };
    var ratio = SR / audioBuffer.sampleRate;
    var outLen = Math.round(audioBuffer.length * ratio);
    var off = new OfflineAudioContext(2, outLen, SR);
    var srcBuf = off.createBuffer(2, audioBuffer.length, audioBuffer.sampleRate);
    srcBuf.copyToChannel(src_L, 0);
    srcBuf.copyToChannel(src_R, 1);
    var node = off.createBufferSource();
    node.buffer = srcBuf;
    node.connect(off.destination);
    node.start();
    var rendered = await off.startRendering();
    return { L: rendered.getChannelData(0).slice(), R: rendered.getChannelData(1).slice(), length: outLen };
}

// ── Возврат в СИСТЕМУ КООРДИНАТ ВЫЗЫВАЮЩЕГО ────────────────────────────
//
// 44100 — паспорт чекпойнта, а не формат ответа. До 07.09.2026 модуль
// молча отдавал стемы на 44100 при ЛЮБОМ входе, и «помнить об этом» было
// обязанностью девяти вызывающих. Не помнил ни один: /vocal-remover
// вычитал 44.1-кГц вокал из 48-кГц оригинала ПОИНДЕКСНО
// (applyStrengthMix), то есть вычитал его на 8.84% быстрее и всё выше
// нарастающим сдвигом — вокал не гасился, а ДОБАВЛЯЛСЯ вторым, ускоренным
// голосом, и последние 8.84% дорожки уходили за конец массива → NaN → тишина.
// Замер на песне с размеченными стемами (public/audio/stem-split, идеальный
// разделитель-заглушка, ползунок 90%): 44100 → вокала осталось −20.33 дБ
// (ровно как обещано), 48000 → −0.97 дБ и SI-SDR −4.57 против +18.13.
// В проде это 705 сессий Ультры за 30 дней (23.8% всех её прогонов).
//
// Поэтому контракт теперь ЯВНЫЙ и держится здесь: split() отдаёт стемы на
// частоте ВХОДНОГО буфера и ровно его длины. Тогда наивное `orig − vocal`
// у любого потребителя верно по построению, а не по памяти автора.
//
// Разностный стем считается в ИСХОДНОЙ области (orig − upsample(pred)), а не
// пересемплируется отдельно. Два следствия, оба нужные:
//   · instrumental + vocal === original ТОЧНО, посемплово — это и есть
//     инвариант, на котором стоит и ползунок силы, и «Скачать микс»;
//   · полоса выше 22.05 кГц, которой модель не видела, остаётся в
//     инструментале, где ей и место, вместо того чтобы пропасть в круговом
//     пересемплировании.
// Отдаёт ГОТОВЫЙ AudioBuffer, а не пару массивов: у OfflineAudioContext
// результат уже AudioBuffer нужной частоты и длины, и лишняя копия здесь стоила
// бы ~230 МБ на десятиминутном файле — на туле, который и так умирает по памяти
// на телефонах (Sentry 117360552, жалоба #1548).
async function toSourceRateBuffer(L, R, outLen, outRate){
    var off = new OfflineAudioContext(2, outLen, outRate);
    var b = off.createBuffer(2, L.length, SR);
    b.copyToChannel(L, 0); b.copyToChannel(R, 1);
    var node = off.createBufferSource();
    node.buffer = b;
    node.connect(off.destination);
    node.start();
    return await off.startRendering();
}

// ── Per-model engine ──
// cfg = { label, nFft, hop, dimF, dimT, compensate, metaName, metaFbName, staticUrls: [] }
// ── Сторож зависшего чанка ──────────────────────────────────────────────
// Скопировано с проверенного соседа (stem-separator.js:521–551): первый чанк
// ждём дольше (в нём компиляция шейдеров), дальше — медиана×6 с полом 60 с.
//
// Почему это ОБЯЗАТЕЛЬНО, а не «на всякий случай»: 18.08.2026 на iPhone
// (iOS 18.7, Safari 26) WebGPU-сессия создалась, модель загрузилась — и
// первый же чанк повис НАВСЕГДА: ни ошибки, ни результата, вечный спиннер.
// Pro-пользователь первого дня написал в поддержку «выдает ошибку», а в
// телеметрии не было НИ ОДНОГО события — зависание без таймаута неотличимо
// от долгого счёта ничем. Тот же класс (webgpu-клин) сосед CascadedNet
// закрыл ещё раньше; сюда починка класса не доехала.
var MDX_CHUNK_TIMEOUT_FIRST_MS = 120000;
var MDX_CHUNK_TIMEOUT_FLOOR_MS = 60000;
// On WASM nothing wedges — the CPU finishes the chunk, a busy or weak device is
// just slower. Kim's first chunk on wasm took >120 s on a loaded desktop
// (24.09.2026) and the precision pass failed with MDX_INFER_TIMEOUT; on
// Android the same code is 8–14× the audio. The wasm bound only has to catch a
// dead proxy worker. Same rule as stem-separator.js.
var MDX_CHUNK_TIMEOUT_WASM_MS = 300000;
function mdxFirstChunkTimeout(provider){
    return (provider && provider !== 'webgpu') ? MDX_CHUNK_TIMEOUT_WASM_MS : MDX_CHUNK_TIMEOUT_FIRST_MS;
}
function mdxAdaptiveChunkTimeout(durations, provider){
    if (provider && provider !== 'webgpu') {
        if (!durations.length) return MDX_CHUNK_TIMEOUT_WASM_MS;
        var sw = durations.slice().sort(function(a, b){ return a - b; });
        return Math.max(MDX_CHUNK_TIMEOUT_WASM_MS, Math.round(sw[Math.floor(sw.length / 2)] * 20));
    }
    if (!durations.length) return MDX_CHUNK_TIMEOUT_FLOOR_MS;
    var s = durations.slice().sort(function(a, b){ return a - b; });
    var median = s[Math.floor(s.length / 2)];
    return Math.max(MDX_CHUNK_TIMEOUT_FLOOR_MS, Math.round(median * 6));
}
// One ORT run at a time on the PAGE — this engine and stem-separator.js share
// one window.ort, and ORT-Web lets a single run() be in flight per runtime (an
// overlap kills both: "Session already started"). Same 6 lines as there, by
// design: both files are raw-loadable and neither may depend on load order.
// The hang timeout starts when the run starts, not while it queues.
function ortRunSerialized(fn){
    var gate = window.__ortRunGate || (window.__ortRunGate = { tail: Promise.resolve() });
    var out = gate.tail.then(fn, fn);
    gate.tail = out.then(function(){}, function(){});
    return out;
}
// VISIBLE time, as in stem-separator.js: a paused phone or a hidden tab is
// not a wedged backend (timbrica-core.js → window.__visibleTimeout).
function mdxAfterVisible(fn, ms){
    if (typeof window.__visibleTimeout === 'function') return window.__visibleTimeout(fn, ms);
    var h = setTimeout(fn, ms);
    return function(){ clearTimeout(h); };
}
function mdxVisibleNow(){
    return (typeof window.__visibleNow === 'function') ? window.__visibleNow()
        : ((typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now());
}
function mdxRunWithTimeout(promise, ms, chunkIdx, lost){
    // No budget — no timer: on the worker lane the PAGE keeps the watch (its
    // clock counts visible time; a worker's timer would fire the moment a
    // frozen phone woke up).
    if (!(ms > 0) && !lost) return promise;
    return new Promise(function(resolve, reject){
        var cancel = (ms > 0) ? mdxAfterVisible(function(){
            var e = new Error('mdx chunk ' + chunkIdx + ' hung >' + Math.round(ms / 1000) + 's (webgpu wedge class)');
            e.code = 'MDX_INFER_TIMEOUT'; e.expected = true;
            reject(e);
        }, ms) : function(){};
        // a lost device answers nothing, ever — the run in flight would only end by the watchdog
        if (lost) lost.then(function(info){
            cancel();
            var e = new Error('mdx: the GPU device was lost (' + String((info && info.reason) || 'unknown') + ') at chunk ' + chunkIdx);
            e.code = 'MDX_GPU_LOST'; e.expected = true; e.chunk = chunkIdx;
            reject(e);
        });
        promise.then(function(v){ cancel(); resolve(v); },
                     function(err){ cancel(); reject(err); });
    });
}

function makeEngine(cfg){
    var N_FFT = cfg.nFft, HOP = cfg.hop, DIM_F = cfg.dimF, DIM_T = cfg.dimT;
    var N_BINS = (N_FFT >> 1) + 1;
    var CHUNK  = HOP * (DIM_T - 1);
    var TRIM   = N_FFT >> 1;
    var GEN    = CHUNK - 2 * TRIM;
    var COMP   = (typeof cfg.compensate === 'number') ? cfg.compensate : 1.0;

    var onnxSession = null, modelLoadPromise = null, modelLoadedSize = 0, WIN = null;
    var sessionProvider = null;   // EP the live session was created for — sizes the hang watchdog
    var sessionGen = -1; // window.__ortGen this session was created under
    function getWindow(){ if (!WIN) WIN = hannPeriodic(N_FFT); return WIN; }

    function modelUrls(){
        if (_wk) return _wk.modelUrls.slice();
        var urls = [], seen = {};
        var push = function(u){ if (u && !seen[u]) { seen[u] = 1; urls.push(u); } };
        if (cfg.metaName)   { var m  = document.querySelector('meta[name="' + cfg.metaName + '"]');   if (m && m.content) push(m.content); }
        if (cfg.metaFbName) { var mf = document.querySelector('meta[name="' + cfg.metaFbName + '"]'); if (mf && mf.content) push(mf.content); }
        (cfg.staticUrls || []).forEach(push);
        return urls;
    }

    async function fetchModelBytes(slug, onProgress, signal){
        var urls = modelUrls(), lastErr = null;
        for (var i = 0; i < urls.length; i++) {
            if (signal && signal.aborted) throw new DOMException('Aborted', 'AbortError');
            try {
                var bytes = await downloadWithResume(urls[i], slug, onProgress, signal);
                modelLoadedSize = bytes.length;
                if (i > 0) reportTelemetry(slug, cfg.label + '_model_fallback', { tried: i, sizeMB: Math.round(bytes.length / 1048576) });
                return bytes;
            } catch (e) {
                if (e && (e.name === 'AbortError' || (signal && signal.aborted))) throw e;
                lastErr = e;
            }
        }
        throw new Error('MDX model unavailable (' + cfg.label + '): ' + (lastErr && lastErr.message || 'all URLs failed'));
    }

    async function _initSession(slug, bytes){
        // The wasm-only build has no WebGPU EP: asking for one there costs a
        // failed provider init per session and changes nothing.
        var hasWebGPU = false;
        if (!ortWasmOnly() && 'gpu' in navigator) { try { hasWebGPU = !!(await navigator.gpu.requestAdapter()); } catch(_){} }
        var providers = hasWebGPU ? ['webgpu', 'wasm'] : ['wasm'];
        // Worker lane: the page may already know that this device's GPU gives up
        // on this model (gpuGaveUpHere) — then the work starts on the CPU.
        if (_wk && _wk.providers && _wk.providers[0] === 'wasm') { providers = ['wasm']; hasWebGPU = false; }
        // Лейн wasm считает в прокси-воркере ORT, а не на главном потоке.
        // session.run на wasm синхронен: один чанк Kim на 2–4-ядерном ПК —
        // 20–120 с, и всё это время страница мертва (LoAF за 30 дней до
        // 09.10.2026: /vocal-remover 54 блока ≥ 5 с, худший 119 с с нажатой
        // «Отменой»; /karaoke 50 с, /lyrics-transcription 65 с, /video-dubbing
        // 44 с — все provider:wasm). Тот же приём, что у CascadedNet
        // (stem-separator.js) с 29.07: прокси без wasmBinary проверен под
        // управляющим Service Worker — 9/9 (память
        // reference_ort_proxy_worker_dies_under_service_worker_2026_08_06).
        // Флаг читается при первой инициализации рантайма: window.ort общий,
        // поэтому WebGPU-лейн его не получает. Вход каждого чанка — новый
        // массив (buildInputTensor): прокси ПЕРЕДАЁТ буфер в воркер.
        // (В лейне-воркере прокси не нужен: счёт и так не на главном потоке.)
        if (!hasWebGPU && !IN_WORKER) {
            try { ort.env.wasm.proxy = true; } catch(_){}
        }
        try {
            onnxSession = await ort.InferenceSession.create(bytes.buffer, {
                executionProviders: providers, graphOptimizationLevel: 'all'
            });
        } catch (e) {
            // The browser could not reserve the current build's memory: ORT 1.18
            // in a fresh runtime (see noShareStepDown). The model bytes are still
            // ours — the runtime died before it was handed anything.
            if (bytes.byteLength && await noShareStepDown(slug, e, cfg.label)) {
                try { ort.env.wasm.proxy = true; } catch(_){}
                providers = ['wasm'];
                try {
                    onnxSession = await ort.InferenceSession.create(bytes.buffer, {
                        executionProviders: providers, graphOptimizationLevel: 'all'
                    });
                    e = null;
                } catch (eLegacy) { e = eLegacy; }
            }
            if (!e) { /* stepped down — falls through to the bookkeeping below */ }
            // A trap while allocating the model (weights don't fit the wasm
            // heap) corrupts the runtime the same way a mid-run trap does.
            else if (isWasmTrap(e)) {
                ortTrapReset(slug, cfg.label + '_create');
                var tc = new Error('mdx wasm runtime trapped during session create: ' + ((e && e.message) || e));
                tc.code = 'ORT_WASM_TRAP'; tc.expected = true; tc.cause = e;
                throw tc;
            }
            else throw e;
        }
        sessionGen = ortGen();
        sessionProvider = providers[0];
        if (sessionProvider === 'webgpu') { if (IN_WORKER) watchGpuLoss(); else watchPageGpuLoss(); }
        reportTelemetry(slug, cfg.label + '_model_loaded', { sizeMB: Math.round(bytes.length / 1048576), provider: providers[0] });
        return onnxSession;
    }

    async function ensureModelLoaded(slug, onProgress, signal){
        // A session created under a runtime generation that has since trapped
        // and been discarded points into freed wasm memory — drop it and
        // rebuild on the fresh runtime instead of reusing a corpse.
        if (onnxSession && sessionGen !== ortGen()) {
            try { onnxSession.release(); } catch(_){}
            onnxSession = null; modelLoadPromise = null;
        }
        if (onnxSession) return onnxSession;
        if (modelLoadPromise) return modelLoadPromise;
        modelLoadPromise = (async function(){
            try {
                if (!wasmSimdSupported()) {
                    reportTelemetry(slug, cfg.label + '_no_simd_gate', {});
                    throw simdRefusal(slug);
                }
                await loadOrtScript();
                var bytes = await fetchModelBytes(slug, onProgress, signal);
                return await _initSession(slug, bytes);
            } catch (e) {
                modelLoadPromise = null;
                throw e;
            }
        })();
        return modelLoadPromise;
    }

    // Bluestein tables (chirp-z FFT for arbitrary N — radix-2 alone corrupts
    // non-power-of-2 nFft). Sized to THIS engine's N_FFT. Float64 throughout.
    var BLUESTEIN_M = 1;
    while (BLUESTEIN_M < 2 * N_FFT - 1) BLUESTEIN_M <<= 1;
    var CHIRP_RE = null, CHIRP_IM = null, B_FFT_RE = null, B_FFT_IM = null;
    function initBluesteinTables(){
        CHIRP_RE = new Float64Array(N_FFT); CHIRP_IM = new Float64Array(N_FFT);
        for (var n = 0; n < N_FFT; n++) {
            var phase = Math.PI * n * n / N_FFT;
            CHIRP_RE[n] = Math.cos(phase); CHIRP_IM[n] = -Math.sin(phase);
        }
        B_FFT_RE = new Float64Array(BLUESTEIN_M); B_FFT_IM = new Float64Array(BLUESTEIN_M);
        for (var n2 = 0; n2 < N_FFT; n2++) { B_FFT_RE[n2] = CHIRP_RE[n2]; B_FFT_IM[n2] = -CHIRP_IM[n2]; }
        for (var n3 = 1; n3 < N_FFT; n3++) { B_FFT_RE[BLUESTEIN_M - n3] = CHIRP_RE[n3]; B_FFT_IM[BLUESTEIN_M - n3] = -CHIRP_IM[n3]; }
        fftInPlace(B_FFT_RE, B_FFT_IM);
    }
    function bluesteinFFT(xRe, xIm){
        if (!CHIRP_RE) initBluesteinTables();
        var aRe = new Float64Array(BLUESTEIN_M), aIm = new Float64Array(BLUESTEIN_M);
        for (var n = 0; n < N_FFT; n++) {
            aRe[n] = xRe[n] * CHIRP_RE[n] - xIm[n] * CHIRP_IM[n];
            aIm[n] = xRe[n] * CHIRP_IM[n] + xIm[n] * CHIRP_RE[n];
        }
        fftInPlace(aRe, aIm);
        for (var k = 0; k < BLUESTEIN_M; k++) {
            var ar = aRe[k], ai = aIm[k], br = B_FFT_RE[k], bi = B_FFT_IM[k];
            aRe[k] = ar * br - ai * bi; aIm[k] = ar * bi + ai * br;
        }
        ifftInPlace(aRe, aIm);
        for (var n2 = 0; n2 < N_FFT; n2++) {
            xRe[n2] = aRe[n2] * CHIRP_RE[n2] - aIm[n2] * CHIRP_IM[n2];
            xIm[n2] = aRe[n2] * CHIRP_IM[n2] + aIm[n2] * CHIRP_RE[n2];
        }
    }
    function bluesteinIFFT(xRe, xIm){
        for (var n = 0; n < N_FFT; n++) xIm[n] = -xIm[n];
        bluesteinFFT(xRe, xIm);
        for (var n2 = 0; n2 < N_FFT; n2++) { xRe[n2] /= N_FFT; xIm[n2] = -xIm[n2] / N_FFT; }
    }

    // ── Быстрый путь: смешанная база + пара каналов за одно преобразование ──
    // ЗАМЕР 2026-08-22 (профиль V8, Chrome с живым WebGPU): из 4689 мс на
    // разделение 7-секундного клипа 60.2% уходило в `fftInPlace` и 0.9% — в
    // саму нейросеть. Узкое место Ультры — наш БПФ, а не модель.
    //   · смешанная база вместо Блюстейна (N=7680 = 2⁹·15): ×4.1
    //   · левый канал в вещественную часть, правый в мнимую:  ×1.8 сверх того
    // Оба множителя померены отдельно (tests/dsp/fft-mixed.test.mjs).
    // Если модуль не подъехал или длина вне класса 2^a·{1,3,5,15} — молча
    // остаёмся на Блюстейне: он медленный, но верный, и это не отказ.
    // План строится ЛЕНИВО и переспрашивается на каждом split(), а не один раз
    // при загрузке модуля: dsp-fft-mixed.js может приехать позже (повторная
    // попытка загрузки через __jsFallback), и жёсткий снимок на момент старта
    // навсегда запер бы движок на медленном пути.
    // FFT_API держится ВМЕСТЕ с планом, а не читается из глобали на каждом
    // кадре: план и реализация — одна вещь, и разъехаться они не должны
    // (первая редакция читала window.DspFftMixed внутри цикла и падала, стоило
    // глобали исчезнуть при живом плане).
    var FFT_PLAN = null, FFT_API = null, _planTried = false;
    function refreshPlan(slug){
        var api = null;
        try { api = (window.DspFftMixed && typeof window.DspFftMixed.plan === 'function') ? window.DspFftMixed : null; }
        catch (_) { api = null; }
        if (!api) { FFT_PLAN = null; FFT_API = null; }
        else if (!FFT_PLAN || FFT_API !== api) {
            try { FFT_PLAN = api.plan(N_FFT); FFT_API = FFT_PLAN ? api : null; }
            catch (_) { FFT_PLAN = null; FFT_API = null; }
        }
        if (!FFT_PLAN && !_planTried) {
            _planTried = true;
            reportTelemetry(slug, cfg.label + '_fft_slowpath', {
                n: N_FFT, has_module: !!api
            });
        }
    }

    // Рабочие буферы — ОДИН НАБОР НА ЗАПУСК, а не на движок и не на кадр.
    // Прежний Блюстейн выделял два Float64Array(16384) на КАЖДЫЙ кадр — 262 КБ
    // обнуления 1024 раза за чанк; переиспользование убирает это целиком.
    // ⚠️ Но привязывать их к ДВИЖКУ нельзя: внутри split() есть точки уступки
    // потока (между чанками и внутри кадровых циклов), и два одновременных
    // split() на одном движке молча перетёрли бы спектр друг друга. Сегодня
    // такого вызывающего нет (варианты /vocal-remover считаются строго по
    // очереди, караоке-разделение — ОТДЕЛЬНЫЙ движок), но модуль общий на семь
    // страниц, и правило «буфер живёт ровно столько, сколько работа» дешевле
    // любой проверки. Один набор на split(), а не на кадр — весь выигрыш
    // сохраняется.
    function makeScratch(){
        return {
            zr: new Float64Array(N_FFT), zi: new Float64Array(N_FFT),
            xr: new Float64Array(N_BINS), xi: new Float64Array(N_BINS),
            yr: new Float64Array(N_BINS), yi: new Float64Array(N_BINS),
        };
    }

    // ── Уступка потока внутри кадрового цикла ────────────────────────────
    // ЗАМЕР 2026-08-22 на проде (PerformanceObserver longtask, 60 с звука,
    // Chrome + WebGPU): 20 длинных задач, суммарно 8.1 с — **59% времени
    // главный поток не отвечал**, самые долгие блоки по 0.5 с, 25 пропусков
    // кадра дольше 100 мс. Считалось это «просто долго», а на деле страница
    // замирала: прокрутка дёргается, кнопка «Отмена» отвечает с задержкой до
    // полусекунды (рядом 579 rage_click за месяц), полоса прогресса стоит.
    // Работы это не убирает — Worker бы убрал, — но нарезка кадрового цикла
    // на куски делает паузы незаметными и стоит примерно ничего.
    //
    // ЗАМЕР ПОСЛЕ (тот же стенд, 60 с звука): поток не отвечает 1–6% времени
    // вместо 59%, самый длинный блок 82–156 мс вместо 526, пропусков кадра
    // дольше 100 мс — единицы вместо 25, отрисованных кадров вдвое больше.
    // Времени это НЕ стоит: парный замер с уступками и без дал лучший прогон
    // 12.5 с против 15.8 — то есть в пределах шума, а не хуже.
    // ⚠️ Шаг 8 / 16 / 24 сравнивались по три прогона: разница между ними
    // ЛОЖИТСЯ В ШУМ машины (соседний суит уводил тот же замер с 4% до 65%),
    // все три радикально лучше исходного. 16 выбран как середина с меньшим
    // числом переключений; подбирать точнее без тихой машины бессмысленно.
    var YIELD_FRAMES = 16;
    function yieldToPaint(){
        return new Promise(function(res){
            if (typeof MessageChannel === 'function') {
                var ch = new MessageChannel();
                ch.port1.onmessage = function(){ ch.port1.close(); res(); };
                ch.port2.postMessage(0);
            } else { setTimeout(res, 0); }
        });
    }

    /** Оба канала разом. Возвращает ту же раскладку, что stftChannel. */
    async function stftPair(sL, sR, sc){
        if (!FFT_PLAN) return { L: stftChannel(sL), R: stftChannel(sR) };
        var Fx = FFT_API;
        var win = getWindow();
        var padL = reflectPad(sL, TRIM), padR = reflectPad(sR, TRIM);
        var outL = new Float32Array(2 * N_BINS * DIM_T);
        var outR = new Float32Array(2 * N_BINS * DIM_T);
        for (var f = 0; f < DIM_T; f++) {
            var off = f * HOP;
            for (var k = 0; k < N_FFT; k++) {
                var w = win[k];
                sc.zr[k] = padL[off + k] * w;
                sc.zi[k] = padR[off + k] * w;
            }
            Fx.fft(FFT_PLAN, sc.zr, sc.zi);
            Fx.untangleTwoReal(sc.zr, sc.zi, N_FFT, N_BINS, sc.xr, sc.xi, sc.yr, sc.yi);
            for (var b = 0; b < N_BINS; b++) {
                var d = 2 * (b * DIM_T + f);
                outL[d] = sc.xr[b]; outL[d + 1] = sc.xi[b];
                outR[d] = sc.yr[b]; outR[d + 1] = sc.yi[b];
            }
            if ((f % YIELD_FRAMES) === YIELD_FRAMES - 1) await yieldToPaint();
        }
        return { L: outL, R: outR };
    }

    function stftChannel(samples){
        var win = getWindow();
        var padded = reflectPad(samples, TRIM);
        var out = new Float32Array(2 * N_BINS * DIM_T);
        var re = new Float64Array(N_FFT), im = new Float64Array(N_FFT);
        for (var f = 0; f < DIM_T; f++) {
            var off = f * HOP;
            for (var k = 0; k < N_FFT; k++) { re[k] = padded[off + k] * win[k]; im[k] = 0; }
            bluesteinFFT(re, im);
            for (var b = 0; b < N_BINS; b++) {
                out[2 * (b * DIM_T + f)    ] = re[b];
                out[2 * (b * DIM_T + f) + 1] = im[b];
            }
        }
        return out;
    }
    function buildInputTensor(spec_L, spec_R){
        var out = new Float32Array(4 * DIM_F * DIM_T);
        for (var b = 0; b < DIM_F; b++) {
            for (var t = 0; t < DIM_T; t++) {
                var src = 2 * (b * DIM_T + t);
                out[(0 * DIM_F + b) * DIM_T + t] = spec_L[src];
                out[(1 * DIM_F + b) * DIM_T + t] = spec_L[src + 1];
                out[(2 * DIM_F + b) * DIM_T + t] = spec_R[src];
                out[(3 * DIM_F + b) * DIM_T + t] = spec_R[src + 1];
            }
        }
        return out;
    }
    function istftChannel(re_bins, im_bins){
        var win = getWindow();
        var paddedLen = CHUNK + 2 * TRIM;
        var out = new Float64Array(paddedLen), norm = new Float64Array(paddedLen);
        var re = new Float64Array(N_FFT), im = new Float64Array(N_FFT);
        for (var f = 0; f < DIM_T; f++) {
            for (var z = 0; z < N_FFT; z++) { re[z] = 0; im[z] = 0; }
            for (var b = 0; b < DIM_F; b++) { re[b] = re_bins[b * DIM_T + f]; im[b] = im_bins[b * DIM_T + f]; }
            for (var b2 = 1; b2 < N_FFT / 2; b2++) { re[N_FFT - b2] = re[b2]; im[N_FFT - b2] = -im[b2]; }
            bluesteinIFFT(re, im);
            var off = f * HOP;
            for (var k = 0; k < N_FFT; k++) { out[off + k] += re[k] * win[k]; norm[off + k] += win[k] * win[k]; }
        }
        for (var i = 0; i < paddedLen; i++) { if (norm[i] > 1e-8) out[i] /= norm[i]; }
        var f32 = new Float32Array(CHUNK);
        for (var i2 = 0; i2 < CHUNK; i2++) f32[i2] = out[TRIM + i2];
        return f32;
    }
    // outDims — форма ВЫХОДНОГО тензора. Шаг между четырьмя плоскостями
    // (L_re, L_im, R_re, R_im) обязан считаться по ней, а не по DIM_F*DIM_T из
    // конфига: у соседнего движка (CascadedNet) выход оказался на один бин
    // шире входа, и предположение «выход как вход» молча сдвигало целый канал.
    // Здесь формы сходятся, поэтому это не починка, а снятие того же допущения.
    function chunkOutputToChannels(predFlat, outDims, sc){
        // Шаг МЕЖДУ плоскостями — по реальной форме тензора; размер того, что
        // отдаём в ISTFT, — по нашей конфигурации. Разводить эти два числа
        // важно: совпадают они только пока выход модели равен входу.
        var planes = (outDims && outDims.length === 4)
            ? outDims[2] * outDims[3]
            : DIM_F * DIM_T;
        var want = DIM_F * DIM_T;
        var take = Math.min(planes, want);
        function plane(i){
            var a = new Float32Array(want);
            a.set(predFlat.subarray(i * planes, i * planes + take));
            return a;
        }
        return istftPair(plane(0), plane(1), plane(2), plane(3), sc);
    }

    /** Обратное преобразование обоих каналов за одно БПФ. */
    async function istftPair(reL, imL, reR, imR, sc){
        if (!FFT_PLAN) return { L: istftChannel(reL, imL), R: istftChannel(reR, imR) };
        var Fx = FFT_API;
        var win = getWindow();
        var paddedLen = CHUNK + 2 * TRIM;
        var outL = new Float64Array(paddedLen), outR = new Float64Array(paddedLen);
        var norm = new Float64Array(paddedLen);
        for (var f = 0; f < DIM_T; f++) {
            for (var b = 0; b < DIM_F; b++) {
                var s = b * DIM_T + f;
                sc.xr[b] = reL[s]; sc.xi[b] = imL[s];
                sc.yr[b] = reR[s]; sc.yi[b] = imR[s];
            }
            Fx.tangleTwoReal(sc.zr, sc.zi, N_FFT, DIM_F, sc.xr, sc.xi, sc.yr, sc.yi);
            Fx.ifft(FFT_PLAN, sc.zr, sc.zi);
            var off = f * HOP;
            for (var k = 0; k < N_FFT; k++) {
                var w = win[k];
                outL[off + k] += sc.zr[k] * w;
                outR[off + k] += sc.zi[k] * w;
                norm[off + k] += w * w;
            }
            if ((f % YIELD_FRAMES) === YIELD_FRAMES - 1) await yieldToPaint();
        }
        var fL = new Float32Array(CHUNK), fR = new Float32Array(CHUNK);
        for (var i = 0; i < paddedLen; i++) { if (norm[i] > 1e-8) { outL[i] /= norm[i]; outR[i] /= norm[i]; } }
        for (var i2 = 0; i2 < CHUNK; i2++) { fL[i2] = outL[TRIM + i2]; fR[i2] = outR[TRIM + i2]; }
        return { L: fL, R: fR };
    }

    function _nowMs(){
        return (typeof performance !== 'undefined' && performance.now)
            ? performance.now() : Date.now();
    }
    // What the DEVICE spent is measured on ACTIVE time (window.__activeNow,
    // timbrica-core.js): wall time minus the stretches the page could not
    // run — a tab frozen in the background, a system asleep. The pace
    // stored below decides whether Ultra is this device's default, and a
    // split that stood an hour in a frozen tab would otherwise teach the
    // page that the device is ten times slower than it is (the same class
    // as /video-fps, prod 26.09.2026: «около 30 ч» at 89 % of a run that
    // had simply stood in a background Opera tab). `wallMs` stays wall.
    function _actNow(){
        return (typeof window !== 'undefined' && typeof window.__activeNow === 'function')
            ? window.__activeNow() : _nowMs();
    }
    /* The user's cancel carries its NAME: a message check read «Aborted()»
       of a dying runtime as a cancel too (window.__isUserCancel). */
    function mdxCancelled() { var e = new Error('Cancelled'); e.name = 'AbortError'; return e; }

    // ── The model's part of a split: 44.1 kHz stereo in, the PRIMARY stem out ──
    // Everything between the two resamplings, and nothing that needs a page —
    // this is the function the worker lane runs in its own thread.
    // ctx = { slug, onProgress, signal, noWatchdog }
    async function _predict(session, L_in, R_in, n_sample, ctx){
        var slug = ctx.slug, onProgress = ctx.onProgress, signal = ctx.signal;
        refreshPlan(slug);
        // Буферы БПФ — на ЭТОТ запуск (см. makeScratch): переиспользуются между
        // кадрами, но не между одновременными split().
        var _scratch = FFT_PLAN ? makeScratch() : null;

        var pad = GEN - (n_sample % GEN);
        if (pad === GEN) pad = 0;

        var padded_L = new Float32Array(TRIM + n_sample + pad + TRIM);
        var padded_R = new Float32Array(TRIM + n_sample + pad + TRIM);
        padded_L.set(L_in, TRIM); padded_R.set(R_in, TRIM);

        var pred_L = new Float32Array(n_sample), pred_R = new Float32Array(n_sample);
        var total_chunks = Math.ceil((n_sample + pad) / GEN), chunk_idx = 0, i = 0;

        // Срок считаем по ФАКТИЧЕСКОЙ скорости этой машины, а не по таблице
        // коэффициентов. Причина: разброс между устройствами больше, чем любая
        // калибровка — на одной видеокарте замерено RTF 0.38, а в один поток
        // WASM 9.56, то есть в 25 раз. Предсказать это по User-Agent нельзя,
        // зато после первого же чанка машина сама себя измерила.
        var _t0 = _nowMs();
        var _a0 = _actNow();
        var _chunkDur = [];   // фактические мс на чанк — кормят адаптивный таймаут

        // ⛔ ПРОВЕРЕНО И ОТВЕРГНУТО 2026-08-22 — совмещение счёта с ожиданием
        // видеокарты. После ускорения БПФ профиль показывал 47.4% простоя
        // главного потока на `session.run`, и напрашивалось переставить нашу
        // работу (обратное преобразование прошлого чанка, прямое следующего) в
        // это окно. Реализовано и замерено на 60-секундном реальном файле,
        // 7 прогонов, RTX 4060 + WebGPU:
        //     без совмещения  — лучший 7.2 с, медиана 8.3 с
        //     с совмещением   — лучший 7.1 с, медиана 7.5 с
        // То есть ноль по лучшему прогону и ~10% по медиане. Плата: в памяти
        // одновременно живут спектр следующего чанка (15.7 МБ) и невыгруженный
        // выход прошлого (12.6 МБ) — около +28 МБ к пику РОВНО на тех
        // устройствах, где Ультра и так падает по памяти (Safari OOM — живой
        // класс отказов этого движка). Плюс усложняется единственный цикл, у
        // которого есть история инцидентов с wasm-трапами и гонкой сессии.
        // Размен признан плохим. Если возвращаться — сначала замерить на
        // СЛАБОЙ видеокарте (там доля простоя больше) и добавить эти 28 МБ в
        // estimateMemoryMB, иначе предупреждение о памяти станет враньём.
        while (i < n_sample + pad) {
            if (signal && signal.aborted) throw mdxCancelled();
            var chunk_L = padded_L.subarray(i, i + CHUNK);
            var chunk_R = padded_R.subarray(i, i + CHUNK);
            var _spec = await stftPair(chunk_L, chunk_R, _scratch);
            var inputArr = buildInputTensor(_spec.L, _spec.R);
            var inputName = session.inputNames && session.inputNames[0] ? session.inputNames[0] : 'input';
            var outName = session.outputNames && session.outputNames[0] ? session.outputNames[0] : null;
            var feeds = {};
            feeds[inputName] = new ort.Tensor('float32', inputArr, [1, 4, DIM_F, DIM_T]);
            var results;
            var _chunkT0 = _nowMs();
            try {
                var _tmo = chunk_idx === 0 ? mdxFirstChunkTimeout(sessionProvider) : mdxAdaptiveChunkTimeout(_chunkDur, sessionProvider), _ci = chunk_idx;
                if (ctx.noWatchdog) _tmo = 0;
                results = await ortRunSerialized(function(){
                    if (signal && signal.aborted) throw mdxCancelled();
                    _chunkT0 = mdxVisibleNow();
                    return mdxRunWithTimeout(session.run(feeds), _tmo, _ci, sessionProvider === 'webgpu' ? pageGpuLostSignal() : null);
                });
                _chunkDur.push(mdxVisibleNow() - _chunkT0);
            } catch (ortErr) {
                var msg = (ortErr && ortErr.message) || '';
                if (isWasmTrap(ortErr)) {
                    // The wasm instance just died mid-inference. Discard the
                    // whole runtime (generation bump) so the NEXT attempt —
                    // this engine, the other MDX engine, or CascadedNet — gets
                    // a fresh heap instead of a guaranteed repeat crash, and
                    // tell the caller in a form the orchestrator can act on.
                    ortTrapReset(slug, cfg.label + '_run_chunk' + chunk_idx);
                    try { session.release(); } catch(_){}
                    onnxSession = null; modelLoadPromise = null;
                    var trapErr = new Error('mdx wasm runtime trapped (memory access out of bounds) at chunk ' + chunk_idx + '/' + total_chunks);
                    trapErr.code = 'ORT_WASM_TRAP'; trapErr.expected = true; trapErr.cause = ortErr;
                    throw trapErr;
                }
                if (/session\s*(mismatch|already\s*started)/i.test(msg)) {
                    var sessErr = new Error('mdx-stem-separator: ONNX session race during inference (' + msg + ')');
                    sessErr.code = 'ORT_SESSION_RACE'; sessErr.expected = true; sessErr.cause = ortErr;
                    throw sessErr;
                }
                // The watchdog fired while the run is STILL going: this chunk
                // cost at least the budget, and split() below turns that into
                // this device's pace.
                if (ortErr && ortErr.code === 'MDX_INFER_TIMEOUT') {
                    ortErr.budgetMs = _tmo; ortErr.chunkSec = GEN / SR; ortErr.provider = sessionProvider || '';
                }
                throw ortErr;
            }
            if (!outName) outName = Object.keys(results)[0];
            var pred = results[outName].data;
            var dec = await chunkOutputToChannels(pred, results[outName].dims, _scratch);
            var write_end = Math.min(GEN, n_sample - i);
            if (write_end > 0) {
                for (var k = 0; k < write_end; k++) { pred_L[i + k] = dec.L[TRIM + k]; pred_R[i + k] = dec.R[TRIM + k]; }
            }
            chunk_idx++;
            if (onProgress) {
                var _frac = chunk_idx / total_chunks;
                var _elapsed = _actNow() - _a0;
                // Третий аргумент — необязательный: прежние вызывающие с
                // сигнатурой (pct, stage) продолжают работать как были.
                onProgress(0.4 + 0.6 * _frac, 'inference', {
                    phase: 'compute',
                    chunks: chunk_idx,
                    total: total_chunks,
                    elapsedMs: Math.round(_elapsed),
                    // Остаток по наблюдаемому темпу + поправка на ХВОСТ.
                    // Счёт по чанкам не видит работу ПОСЛЕ цикла (обратное
                    // преобразование, склейка дорожек, создание буферов), и
                    // без поправки срок систематически занижался: сверено на
                    // живом прогоне 14.08 (Kim, WebGPU, 11 чанков) — промах
                    // −22%…−9% ВЕЗДЕ в сторону «обещали быстрее, чем вышло».
                    //
                    // 1.18 — не подогнанная константа, а сознательный запас:
                    // повторный прогон той же конфигурации занял 18.6 с
                    // против 28.9 у первого, то есть темп плавает внутри
                    // самой машины, и точнее ±25% тут не сделать никаким
                    // коэффициентом. Запас выбран так, чтобы ошибка легла в
                    // сторону «обещали дольше, чем вышло»: пользователь
                    // получает результат раньше обещанного, а не позже.
                    // Поправка гасится вместе с остатком и не раздувает
                    // последние секунды.
                    etaSec: _frac > 0
                        ? Math.round(1.18 * _elapsed * (1 - _frac) / _frac / 1000)
                        : 0,
                    rateRealtime: (chunk_idx * GEN) > 0
                        ? +((_elapsed / 1000) / (chunk_idx * GEN / SR)).toFixed(3)
                        : 0
                });
            }
            i += GEN;
            // Не таймером: в скрытой вкладке он будит раз в секунду, а после
            // 5 минут скрытия — раз в минуту, то есть до минуты простоя на КАЖДЫЙ
            // чанк песни (у 4-минутной их ~40).
            await yieldToPaint();
        }

        return { L: pred_L, R: pred_R, chunks: total_chunks, t0: _t0, a0: _a0, firstMs: _chunkDur.length ? Math.round(_chunkDur[0]) : 0 };
    }

    // ── The page's part: the two stems, in the caller's sample rate ──
    // st = { t0, a0, chunks } of the run that produced `pred_*` (+ lane, provider
    // on the worker lane).
    async function _finish(audioBuffer, pred_L, pred_R, n_sample, slug, st){
        // The net predicts the model's PRIMARY stem (pred·comp); the other stem is
        // the residual (mix − pred·comp). WHICH stem is primary is a per-model fact
        // (cfg.primary): Kim_Vocal_2 predicts VOCALS, KARA_2 predicts inst+backing.
        // 2026-07-23: Kim was shipped inverted for ~2 months (705faba67 assigned
        // instrumental=pred based on an energy-budget check that is label-symmetric
        // and cannot distinguish polarity). Ground truth measured three ways on the
        // prod bytes (SHA ce74ef3b…): torch-STFT reference voiceband 0.92, in-browser
        // probe voiceband 0.946 on this very pipeline, and by ear. Do NOT re-derive
        // polarity from energy budgets — only content metrics (voiceband/listening).
        var priL = pred_L, priR = pred_R;
        if (COMP !== 1.0) {
            priL = new Float32Array(n_sample); priR = new Float32Array(n_sample);
            for (var c = 0; c < n_sample; c++) { priL[c] = pred_L[c] * COMP; priR[c] = pred_R[c] * COMP; }
        }

        // Обратно в частоту вызывающего (см. toSourceRateBuffer). Остаток
        // считается ПОСЛЕ возврата и в ИСХОДНОЙ области — так `pri + res` даёт
        // вход посемплово, а полоса выше 22.05 кГц, которой модель не видела,
        // остаётся в остатке, а не пропадает в круговом пересемплировании.
        var outRate = audioBuffer.sampleRate, outLen = audioBuffer.length;
        var Ctor = window.AudioBuffer;
        var priBuf;
        if (outRate === SR && outLen === n_sample) {
            priBuf = new Ctor({ length: outLen, numberOfChannels: 2, sampleRate: outRate });
            priBuf.copyToChannel(priL, 0); priBuf.copyToChannel(priR, 1);
        } else {
            priBuf = await toSourceRateBuffer(priL, priR, outLen, outRate);
            priL = priR = null;            // 44.1-кГц копии больше не нужны
        }
        var srcL = audioBuffer.getChannelData(0);
        var srcR = audioBuffer.numberOfChannels > 1 ? audioBuffer.getChannelData(1) : srcL;
        var pL = priBuf.getChannelData(0), pR = priBuf.getChannelData(1);
        var resBuf = new Ctor({ length: outLen, numberOfChannels: 2, sampleRate: outRate });
        var rL = resBuf.getChannelData(0), rR = resBuf.getChannelData(1);
        for (var k2 = 0; k2 < outLen; k2++) { rL[k2] = srcL[k2] - pL[k2]; rR[k2] = srcR[k2] - pR[k2]; }

        // Темп ЭТОЙ машины запоминается на устройстве: это единственный
        // честный вход для решения «включать ли Ультра по умолчанию в
        // следующий раз» (см. defaultEngineVerdict в хвосте файла). Класс
        // устройства таким входом не является — замер 15–18.08: WebGPU-
        // устройство есть у 78% телефонов, а реальный темп там wasm-класса.
        var _wallMs = Math.round(_nowMs() - st.t0);
        var _activeMs = Math.round(_actNow() - st.a0);
        var _rate = +(((_activeMs / 1000) / (n_sample / SR)).toFixed(3));
        try {
            localStorage.setItem(rateKey(cfg.label), String(_rate));
            localStorage.removeItem('mdx_rate_' + cfg.label);   // замер прежнего движка
        } catch (_) {}
        var _ok = { chunks: st.chunks, durationSec: n_sample / SR, modelMB: Math.round((st.modelBytes || modelLoadedSize) / 1048576), wallMs: _wallMs, activeMs: _activeMs, rate: _rate };
        if (st.lane) { _ok.lane = st.lane; _ok.provider = st.provider || ''; }
        // The first chunk carries the shader compile and the warm-up: how long it takes on
        // devices that DO finish is what the first-chunk watchdog (120 s on WebGPU) is judged by.
        if (st.firstMs > 0) _ok.firstMs = st.firstMs;
        // quiet — a caller that walks a recording in windows (crowd-removal.js)
        // reports the whole walk itself: one event per run, not one per minute.
        if (!st.quiet) reportTelemetry(slug, cfg.label + '_split_ok', _ok);
        return (cfg.primary === 'vocal')
            ? { instrumental: resBuf, vocal: priBuf }
            : { instrumental: priBuf, vocal: resBuf };
    }


    async function _splitInner(audioBuffer, opts){
        opts = opts || {};
        var slug = opts.slug || 'vocal-remover';
        var onProgress = opts.onProgress;
        var signal = opts.signal;

        // Этап — КОДОМ, как у StemSeparator ('loading-model' / 'inference'), текст
        // рисует страница из своих переводов. До 30.09.2026 здесь стояли русские
        // строки, и /vocal-remover и /karaoke показывали «Расчёт 3/11» на всех
        // локалях. phase в третьем аргументе — для сторожей «загрузка зависла»:
        // долгий чанк на WASM (до минуты) — это расчёт, а не обрыв сети.
        await ensureModelLoaded(slug, function(p){ if (onProgress) onProgress(p * 0.4, 'loading-model', { phase: 'download', frac: p }); }, signal);
        var session = onnxSession;

        var resampled = await ensure44100Stereo(audioBuffer);
        var pred = await _predict(session, resampled.L, resampled.R, resampled.length, { slug: slug, onProgress: onProgress, signal: signal });
        pred.quiet = !!opts.quiet;
        return _finish(audioBuffer, pred.L, pred.R, resampled.length, slug, pred);
    }

    // ── Обёртка отказа вокруг split ────────────────────────────────────
    // 18.08 у отказа не было НИ телеметрии, ни следа: пользовательская
    // жалоба «выдает ошибку» не имела ни одного события рядом, и 89.5%
    // мобильных Ultra-сессий уходили ни с чем 4 дня незамеченными. Теперь
    // модуль сам (для ВСЕХ страниц-потребителей):
    //   · шлёт маркер <label>_split_failed с причиной;
    //   · взводит сессионный предохранитель — defaultEngineVerdict больше
    //     не предложит Ультра в этой сессии (ручной выбор не ограничен);
    //   · при зависании (MDX_INFER_TIMEOUT) сбрасывает клин-сессию, чтобы
    //     следующая попытка не упёрлась в тот же мёртвый бекенд.
    // Отмена пользователем отказом НЕ считается.
    //
    // ⚠️⚠️ Зависание — это не «сессия плохая», а «прогон всё ещё идёт». ORT-web
    // пускает ОДИН run() на весь рантайм (замок живёт внутри wasm-модуля и
    // снимается только когда прогон закончится), а рантайм на странице общий с
    // CascadedNet. До 27.09.2026 здесь делалось session.release() — рантайм
    // оставался, и запасной движок, на который страница откатывается, садился
    // на тот же замок: «Session already started» сразу после
    // MDX_INFER_TIMEOUT — КАЖДЫЙ такой отказ /remove-word-from-song с 18 по
    // 27.09 (слабые десктопы: Яндекс/Edge/Opera, 2–4 ядра, встроенная графика —
    // первый чанк Kim дольше двух минут). А release() под живым прогоном ещё и
    // освобождает память, которую прогон использует (стенд: «table index is
    // out of bounds» в том же рантайме). Поэтому: сессию не трогаем, рантайм
    // бросаем целиком — следующий потребитель поднимет свежий (тот же ход, что
    // CascadedNet делает на своём зависании). И запоминаем темп: чанк стоил не
    // меньше бюджета сторожа, то есть это устройство медленное для Ультры, и
    // вердикт в следующий раз скажет «нет», не заставляя ждать ещё две минуты.
    // Стенд — tests/playwright/remove-word-slow-precision-engine.mjs.
    function abandonHungRuntime(slug, e){
        onnxSession = null; modelLoadPromise = null; sessionProvider = null;
        ortTrapReset(slug, cfg.label + '_hang');
        paceFromTimeout(slug, e);
    }
    // The device of this runtime is gone: the session cannot run again, the runtime is dropped like a
    // hung one (the fallback engine gets a fresh one and a fresh device) — but no «slow» pace is written:
    // a lost device is not a slow one. An early loss teaches the device (as on the worker lane).
    function abandonLostRuntime(slug, e){
        onnxSession = null; modelLoadPromise = null; sessionProvider = null;
        ortTrapReset(slug, cfg.label + '_gpu_lost');
        if ((e.chunk | 0) <= 2) { try { localStorage.setItem(gpuBadKey(), String(Date.now())); } catch (_) {} }
        reportTelemetry(slug, cfg.label + '_gpu_gave_up', { why: 'lost', chunk: e.chunk | 0, lane: 'page' });
    }
    function paceFromTimeout(slug, e){
        if (e && e.budgetMs > 0 && e.chunkSec > 0) {
            var slow = +((e.budgetMs / 1000) / e.chunkSec).toFixed(3);
            try {
                var prev = parseFloat(localStorage.getItem(rateKey(cfg.label)));
                if (!(isFinite(prev) && prev > slow)) localStorage.setItem(rateKey(cfg.label), String(slow));
            } catch (_) {}
            reportTelemetry(slug, cfg.label + '_pace_from_timeout', { rate: slow, provider: e.provider || '' });
        }
    }
    // ── The worker lane, page side ──────────────────────────────────────────
    // split(buffer, { lane: 'worker', gpuRetry }) — the same stems, computed in a
    // thread of its own. The caller asks for it; a page without a worker URL
    // (<meta name="mdx-worker-url">, built by asset-router's workerWithDeps)
    // gets the page lane, as before.
    //   · The page resamples (a worker has no OfflineAudioContext) and keeps the
    //     watch: the worker reports every chunk, and SILENCE longer than the
    //     chunk's budget — on VISIBLE time — ends it. Ending is terminate(): the
    //     hung run stops and its memory goes back, where the page lane can only
    //     abandon a runtime that keeps running (abandonHungRuntime above).
    //   · A GPU that gave up — a hang, a lost device, a failed allocation — is
    //     answered on the CPU within the SAME call and remembered for 30 days:
    //     the next split on this device starts there instead of waiting a
    //     minute to learn it again. Prod 13.10.2026 (support chat 684): one
    //     phone, nine page loads, nine «chunk 1 hung >60s», no music under a
    //     single re-voiced line; RU, 30 days, Kim on a phone's WebGPU finished
    //     in 29–32 % of sessions against 74–81 % on desktops.
    //   · free() terminates the worker — that is what returns the memory.
    var GPU_BAD_TTL_MS = 30 * 24 * 3600 * 1000;
    // A cancel arrives as an AbortSignal — or as the page's own {get aborted(){…}}
    // (/vocal-remover, /video-crop): the page lane only ever reads .aborted, and until
    // 15.10.2026 the worker lane called addEventListener on it and failed at once
    // («g.addEventListener is not a function») — the whole Ultra went to the fallback.
    // → a function that stops watching.
    function watchAbort(signal, onAbort){
        if (!signal) return function(){};
        if (typeof signal.addEventListener === 'function') {
            signal.addEventListener('abort', onAbort);
            return function(){ try { signal.removeEventListener('abort', onAbort); } catch (_) {} };
        }
        var poll = setInterval(function(){ if (signal.aborted) { clearInterval(poll); onAbort(); } }, 250);
        return function(){ clearInterval(poll); };
    }
    var LANE_BOOT_SILENCE_MS = 120000;   // no byte of the model and no session for this long
    var _lane = null, _laneTail = Promise.resolve();
    function workerUrl(){
        try { var m = document.querySelector('meta[name="mdx-worker-url"]'); return (m && m.content) || ''; }
        catch (_) { return ''; }
    }
    function gpuBadKey(){ return 'mdx_gpu_bad_' + cfg.label; }
    function gpuGaveUpHere(){
        try { var t = parseInt(localStorage.getItem(gpuBadKey()), 10); return isFinite(t) && (Date.now() - t) < GPU_BAD_TTL_MS; }
        catch (_) { return false; }
    }
    function laneClose(){
        if (!_lane) return;
        try { _lane.worker.terminate(); } catch (_) {}
        _lane = null;
    }
    function laneError(code, message, extra){
        var e = new Error(message);
        e.code = code; e.expected = true; e.lane = 'worker';
        for (var k in (extra || {})) if (Object.prototype.hasOwnProperty.call(extra, k)) e[k] = extra[k];
        return e;
    }
    // Why the GPU gave up, or '' when the failure is not the GPU's.
    function gpuGaveUp(e){
        if (!e || e.lane !== 'worker' || e.cpuOnly) return '';
        if (e.code === 'MDX_GPU_LOST') return 'lost';
        if (e.provider === 'webgpu') {
            if (e.code === 'MDX_INFER_TIMEOUT') return 'hang';
            if (e.code === 'MDX_WORKER_CRASH') return 'crash';
        }
        // the session never came up although the model had arrived
        if (e.code === 'MDX_WORKER_BOOT_TIMEOUT' && e.afterDownload) return 'hang';
        return /GPUDevice|GPUBuffer|createBuffer|mapAsync|webgpu|device (is |was )?lost/i.test(String(e.message || '')) ? 'alloc' : '';
    }
    // One run at a time: the worker serves the calls of one job (the windows of
    // one recording) one after another.
    function laneRun(slug, L, R, n, opts, providers){
        var run = _laneTail.then(function(){ return laneRunNow(slug, L, R, n, opts || {}, providers); });
        _laneTail = run.then(function(){}, function(){});
        return run;
    }
    function laneRunNow(slug, L, R, n, opts, providers){
        return new Promise(function(resolve, reject){
            var onProgress = opts.onProgress, signal = opts.signal;
            var cpuOnly = !!(providers && providers[0] === 'wasm');
            if (_lane && _lane.cpuOnly !== cpuOnly) laneClose();   // another provider plan — another worker
            var lane = _lane, fresh = !lane;
            if (fresh) {
                var w;
                try { w = new Worker(workerUrl()); }
                catch (eW) { reject(laneError('MDX_WORKER_UNAVAILABLE', 'mdx worker could not start: ' + ((eW && eW.message) || eW), { cpuOnly: cpuOnly })); return; }
                lane = _lane = { worker: w, provider: '', cpuOnly: cpuOnly, ready: false, modelBytes: 0 };
            }
            var done = false, stopWatch = null, unwatchAbort = null, lastAt = mdxVisibleNow(), durs = [], chunk = 0;
            var phase = lane.ready ? 'compute' : 'boot', downloaded = 0, t0 = 0, a0 = 0;
            function budget(){
                if (phase === 'boot') return LANE_BOOT_SILENCE_MS;
                return chunk === 0 ? mdxFirstChunkTimeout(lane.provider) : mdxAdaptiveChunkTimeout(durs, lane.provider);
            }
            function arm(){ if (stopWatch) stopWatch(); stopWatch = mdxAfterVisible(onSilence, budget()); }
            function kill(){ if (_lane === lane) laneClose(); else { try { lane.worker.terminate(); } catch (_) {} } }
            function settle(fn, v){
                if (done) return;
                done = true;
                if (stopWatch) stopWatch();
                if (unwatchAbort) unwatchAbort();
                lane.worker.onmessage = null; lane.worker.onerror = null;
                fn(v);
            }
            function onSilence(){
                var ms = budget();
                kill();
                settle(reject, phase === 'boot'
                    ? laneError('MDX_WORKER_BOOT_TIMEOUT', 'mdx worker: no model byte and no session for ' + Math.round(ms / 1000) + 's',
                        { provider: lane.provider, cpuOnly: cpuOnly, afterDownload: downloaded >= 1 })
                    : laneError('MDX_INFER_TIMEOUT', 'mdx chunk ' + chunk + ' hung >' + Math.round(ms / 1000) + 's (webgpu wedge class)',
                        { budgetMs: ms, chunkSec: GEN / SR, provider: lane.provider, chunk: chunk, cpuOnly: cpuOnly }));
            }
            function onAbort(){ kill(); settle(reject, mdxCancelled()); }
            function send(){
                phase = 'compute'; lastAt = mdxVisibleNow(); arm();
                t0 = _nowMs(); a0 = _actNow();      // the device's pace is the model's work, not the download
                // The originals stay when a GPU that gives up is answered on the CPU in this call;
                // a call that cannot retry hands over its own resampled copy (3:38 at 48 kHz: −77 MB at the peak).
                var l = opts.transferInput ? L : L.slice(), r = opts.transferInput ? (R === L ? L.slice() : R) : R.slice();
                lane.worker.postMessage({ type: 'predict', L: l, R: r, n: n }, [l.buffer, r.buffer]);
            }
            lane.worker.onerror = function(ev){
                kill();
                settle(reject, laneError('MDX_WORKER_CRASH', 'mdx worker crashed: ' + String((ev && ev.message) || 'error').slice(0, 160),
                    { provider: lane.provider, chunk: chunk, cpuOnly: cpuOnly }));
            };
            lane.worker.onmessage = function(ev){
                var m = ev.data || {};
                if (m.type === 'tele') {
                    var pl = { lane: 'worker' };
                    for (var k in (m.payload || {})) if (Object.prototype.hasOwnProperty.call(m.payload, k)) pl[k] = m.payload[k];
                    reportTelemetry(m.slug || slug, m.action, pl);
                } else if (m.type === 'progress' && m.phase === 'download') {
                    downloaded = m.frac; arm();
                    if (onProgress) onProgress(m.frac * 0.4, 'loading-model', { phase: 'download', frac: m.frac });
                } else if (m.type === 'progress') {
                    var now = mdxVisibleNow();
                    durs.push(now - lastAt); lastAt = now; chunk++; arm();
                    if (onProgress) onProgress(m.frac, 'inference', m.info || { phase: 'compute' });
                } else if (m.type === 'ready') {
                    lane.ready = true; lane.provider = m.provider || ''; lane.modelBytes = m.bytes || 0;
                    send();
                } else if (m.type === 'gpu_lost') {
                    kill();
                    settle(reject, laneError('MDX_GPU_LOST', 'mdx: the GPU device was lost (' + (m.reason || 'unknown') + ')',
                        { provider: 'webgpu', chunk: chunk, cpuOnly: cpuOnly }));
                } else if (m.type === 'result') {
                    settle(resolve, { L: m.L, R: m.R, chunks: m.chunks, provider: lane.provider, modelBytes: lane.modelBytes, t0: t0, a0: a0,
                        firstMs: durs.length ? Math.round(durs[0]) : 0 });
                } else if (m.type === 'error') {
                    kill();
                    var e = laneError(m.code || 'MDX_WORKER_ERROR', m.message || 'mdx worker error',
                        { provider: m.provider || lane.provider, chunk: chunk, cpuOnly: cpuOnly });
                    if (m.name === 'AbortError') e.name = 'AbortError';
                    settle(reject, e);
                }
            };
            if (signal) {
                if (signal.aborted) { onAbort(); return; }
                unwatchAbort = watchAbort(signal, onAbort);
            }
            if (!fresh) { send(); return; }
            arm();
            lane.worker.postMessage({ type: 'boot', label: cfg.label, slug: slug, modelUrls: modelUrls(), providers: providers || null,
                ort: { primary: ortPrimary(), fallback: ortFallback(), wasmBase: ortWasmBase(), wasmOnly: ortWasmOnly(), legacy: !!ortLegacy() } });
        });
    }
    // preload(slug, { lane: 'worker' }) — the model loads in the worker that will run it.
    // A page that warms Kim ahead of the file (/vocal-remover: on file pick) and then asks
    // for the worker lane would otherwise hold TWO copies: measured 15.10.2026 on the live
    // page, 3:38 song, RTX 4060 — page lane peak +1265 MB, worker lane with a page-side
    // preload +2641…+2890 MB and 137–167 s against 94 s.
    function laneWarm(slug, opts){
        var run = _laneTail.then(function(){ return laneWarmNow(slug, opts || {}); });
        _laneTail = run.then(function(){}, function(){});
        return run;
    }
    function laneWarmNow(slug, opts){
        return new Promise(function(resolve, reject){
            var providers = gpuGaveUpHere() ? ['wasm'] : null, cpuOnly = !!providers;
            if (_lane && _lane.cpuOnly !== cpuOnly) laneClose();
            if (_lane && _lane.ready) { resolve(true); return; }
            var w;
            try { w = new Worker(workerUrl()); }
            catch (eW) { reject(laneError('MDX_WORKER_UNAVAILABLE', 'mdx worker could not start: ' + ((eW && eW.message) || eW), { cpuOnly: cpuOnly })); return; }
            var lane = _lane = { worker: w, provider: '', cpuOnly: cpuOnly, ready: false, modelBytes: 0 };
            var done = false, stopWatch = null, unwatchAbort = null, downloaded = 0;
            function arm(){ if (stopWatch) stopWatch(); stopWatch = mdxAfterVisible(onSilence, LANE_BOOT_SILENCE_MS); }
            function kill(){ if (_lane === lane) laneClose(); else { try { lane.worker.terminate(); } catch (_) {} } }
            function settle(fn, v){
                if (done) return;
                done = true;
                if (stopWatch) stopWatch();
                if (unwatchAbort) unwatchAbort();
                lane.worker.onmessage = null; lane.worker.onerror = null;
                fn(v);
            }
            function onSilence(){
                kill();
                settle(reject, laneError('MDX_WORKER_BOOT_TIMEOUT', 'mdx worker: no model byte and no session for ' + Math.round(LANE_BOOT_SILENCE_MS / 1000) + 's',
                    { provider: lane.provider, cpuOnly: cpuOnly, afterDownload: downloaded >= 1 }));
            }
            function onAbort(){ kill(); settle(reject, mdxCancelled()); }
            lane.worker.onerror = function(ev){
                kill();
                settle(reject, laneError('MDX_WORKER_CRASH', 'mdx worker crashed: ' + String((ev && ev.message) || 'error').slice(0, 160), { cpuOnly: cpuOnly }));
            };
            lane.worker.onmessage = function(ev){
                var m = ev.data || {};
                if (m.type === 'tele') {
                    var pl = { lane: 'worker' };
                    for (var k in (m.payload || {})) if (Object.prototype.hasOwnProperty.call(m.payload, k)) pl[k] = m.payload[k];
                    reportTelemetry(m.slug || slug, m.action, pl);
                } else if (m.type === 'progress' && m.phase === 'download') {
                    downloaded = m.frac; arm();
                    if (opts.onProgress) { try { opts.onProgress(m.frac); } catch (_) {} }
                } else if (m.type === 'ready') {
                    lane.ready = true; lane.provider = m.provider || ''; lane.modelBytes = m.bytes || 0;
                    settle(resolve, true);
                } else if (m.type === 'gpu_lost' || m.type === 'error') {
                    kill();
                    var e = laneError(m.type === 'gpu_lost' ? 'MDX_GPU_LOST' : (m.code || 'MDX_WORKER_ERROR'),
                        m.type === 'gpu_lost' ? 'mdx: the GPU device was lost (' + (m.reason || 'unknown') + ')' : (m.message || 'mdx worker error'),
                        { provider: m.provider || lane.provider, cpuOnly: cpuOnly });
                    if (m.name === 'AbortError') e.name = 'AbortError';
                    settle(reject, e);
                }
            };
            if (opts.signal) {
                if (opts.signal.aborted) { onAbort(); return; }
                unwatchAbort = watchAbort(opts.signal, onAbort);
            }
            arm();
            lane.worker.postMessage({ type: 'boot', label: cfg.label, slug: slug, modelUrls: modelUrls(), providers: providers,
                ort: { primary: ortPrimary(), fallback: ortFallback(), wasmBase: ortWasmBase(), wasmOnly: ortWasmOnly(), legacy: !!ortLegacy() } });
        });
    }
    async function splitInWorker(audioBuffer, opts){
        opts = opts || {};
        var slug = opts.slug || 'vocal-remover';
        if (!wasmSimdSupported()) {
            reportTelemetry(slug, cfg.label + '_no_simd_gate', {});
            throw simdRefusal(slug);
        }
        var resampled = await ensure44100Stereo(audioBuffer);
        var n = resampled.length;
        var cpuFirst = gpuGaveUpHere();
        var pred;
        // A whole recording is not retried on the CPU here (gpuRetry: false), and an input that is a
        // fresh resampled copy — not the AudioBuffer's own channels — can go to the worker as it is.
        var handOver = opts.gpuRetry === false && resampled.L !== audioBuffer.getChannelData(0);
        try {
            pred = await laneRun(slug, resampled.L, resampled.R, n, handOver ? Object.assign({}, opts, { transferInput: true }) : opts, cpuFirst ? ['wasm'] : null);
        } catch (e) {
            var why = gpuGaveUp(e), b = ortBuild();
            if (why) {
                // A caller that asked for a WHOLE recording may not want it on the
                // CPU (ten times the recording's length): it says so and decides.
                // A GPU that carried such a run for a while and then stopped is not
                // «a GPU that cannot do this model» — it is one that cannot do it
                // for long, and the pace written by the timeout (paceFromTimeout)
                // already sends this device to windows. Only an early stop teaches
                // the device's memory here; the windows will, if they meet it too.
                var whole = opts.gpuRetry === false;
                if (!whole || (e.chunk | 0) <= 2) { try { localStorage.setItem(gpuBadKey(), String(Date.now())); } catch (_) {} }
                reportTelemetry(slug, cfg.label + '_gpu_gave_up', { why: why, chunk: e.chunk | 0, lane: 'worker', whole: whole ? 1 : 0 });
                if (whole) throw e;
            } else if (!ortLegacy() && b && typeof b.initFailed === 'function' && b.initFailed(e, { legacy: '', from: cfg.label + '_worker' })) {
                // the browser could not reserve the current build's memory: the
                // owner now answers ORT 1.18, and the next boot message carries it
                reportTelemetry(slug, 'ort_noshare_stepdown', { from: cfg.label + '_worker', legacy: 1 });
            } else { throw e; }
            // the pace this device is judged by is the lane that finished; a handed-over input is gone
            if (handOver) resampled = await ensure44100Stereo(audioBuffer);
            pred = await laneRun(slug, resampled.L, resampled.R, n, opts, ['wasm']);
        }
        // release: true — the caller needs this model no more: the worker goes NOW, before the page builds
        // the two stems (2 copies of the song). Closed after them, its heap and the page's stems overlapped:
        // 15.10.2026, 3:38 at 48 kHz, the renderer peaked +823…+828 MB against +691…+731 on the page lane.
        if (opts.release) laneClose();
        return _finish(audioBuffer, pred.L, pred.R, n, slug,
            { t0: pred.t0, a0: pred.a0, chunks: pred.chunks, lane: 'worker', provider: pred.provider, modelBytes: pred.modelBytes, quiet: !!opts.quiet, firstMs: pred.firstMs });
    }

    async function split(audioBuffer, opts){
        try {
            if (opts && opts.lane === 'worker' && typeof Worker !== 'undefined' && workerUrl()) return await splitInWorker(audioBuffer, opts);
            return await _splitInner(audioBuffer, opts);
        } catch (e) {
            var msg = String((e && e.code) || (e && e.message) || e);
            /* A cancel is the caller's signal — not «cancel|abort» in the text:
               a runtime dying with «Aborted()» skipped the failure record and the
               fuse (sessionStorage), so the next split walked into it again. */
            var sig = opts && opts.signal;
            var cancelled = window.__isUserCancel ? window.__isUserCancel(e, sig)
                : !!(sig ? sig.aborted : (e && e.name === 'AbortError'));
            if (!cancelled) {
                try { sessionStorage.setItem('mdx_split_failed_' + cfg.label, msg.slice(0, 80)); } catch (_) {}
                reportTelemetry((opts && opts.slug) || 'vocal-remover',
                    cfg.label + '_split_failed', { reason: msg.slice(0, 120) });
                // On the worker lane the hung run died with its worker — there is
                // no runtime to abandon, only the pace to remember.
                if (e && e.code === 'MDX_INFER_TIMEOUT') {
                    if (e.lane === 'worker') paceFromTimeout((opts && opts.slug) || 'vocal-remover', e);
                    else abandonHungRuntime((opts && opts.slug) || 'vocal-remover', e);
                } else if (e && e.code === 'MDX_GPU_LOST' && e.lane !== 'worker') {
                    abandonLostRuntime((opts && opts.slug) || 'vocal-remover', e);
                }
            }
            throw e;
        }
    }

    return {
        preload: function(slug, onProgressOrOpts){
            var o = (typeof onProgressOrOpts === 'function') ? { onProgress: onProgressOrOpts } : (onProgressOrOpts || {});
            if (o.lane === 'worker' && typeof Worker !== 'undefined' && workerUrl()) {
                if (!wasmSimdSupported()) return Promise.reject(simdRefusal(slug || 'vocal-remover'));
                return laneWarm(slug || 'vocal-remover', o);
            }
            return ensureModelLoaded(slug, o.onProgress, o.signal);
        },
        split: split,
        isWarm: function(){ return !!onnxSession || !!(_lane && _lane.ready); },
        /** Seconds of sound one run of the model yields: a shorter window costs the same run. */
        chunkSec: GEN / SR,
        /**
         * Regions that need separating → the windows to hand to split().
         * A caller that needs the stems only in places (under a muted word, under a
         * re-voiced line) pays for those places, not for the recording — on a slow
         * device that is the difference between seconds and the whole file.
         *   regions  [[a, b], …] seconds;  ctxS — context the model must see on
         *   either side of a region (default 0.3 s; the chunk's own edge trim is 87 ms).
         * Each window is a WHOLE number of runs, centred on its regions: the model
         * computes a full chunk whatever it is given, so the spare seconds are real
         * sound around the region instead of the zeros a short window is padded
         * with (measured on the censor lane, 24.09.2026: the voice left under the
         * music −28.0 dB against −26.7). Windows that touch are merged.
         * → [{ a, b, runs, regions: [[a, b], …] }], a ≥ 0, b ≤ durationSec.
         */
        windowsFor: function(regions, durationSec, ctxS){
            var chunk = GEN / SR, ctx = (typeof ctxS === 'number' && ctxS >= 0) ? ctxS : 0.3;
            var rs = (regions || []).map(function(r){ return [Math.max(0, r[0]), Math.min(durationSec, r[1])]; })
                .filter(function(r){ return r[1] > r[0]; })
                .sort(function(x, y){ return x[0] - y[0]; });
            function fit(group){
                var ra = group[0][0], rb = group[group.length - 1][1];
                var runs = Math.max(1, Math.ceil((rb - ra + 2 * ctx - 1e-9) / chunk));
                var len = Math.min(durationSec, runs * chunk);
                runs = Math.max(1, Math.ceil(len / chunk - 1e-9));   // a recording shorter than its windows costs what it holds
                var a = (ra + rb) / 2 - len / 2;
                a = Math.max(0, Math.min(a, durationSec - len));
                return { a: a, b: a + len, runs: runs, regions: group };
            }
            var out = [];
            for (var i = 0; i < rs.length; i++) {
                var w = fit([rs[i]]);
                while (out.length && out[out.length - 1].b >= w.a) w = fit(out.pop().regions.concat(w.regions));
                out.push(w);
            }
            return out;
        },
        /** true while this device's GPU is remembered as giving up on this model (worker lane). */
        gpuGaveUpHere: gpuGaveUpHere,
        /* What the worker lane serves from inside its own thread (see the end of the file). */
        _serve: IN_WORKER ? {
            ensure: ensureModelLoaded,
            predict: function(L, R, n, ctx){ return _predict(onnxSession, L, R, n, ctx); },
            provider: function(){ return sessionProvider || ''; },
            bytes: function(){ return modelLoadedSize; }
        } : null,
        size: function(){ return modelLoadedSize; },
        /* free() — drop the ONNX session so its arena can be REUSED.
         *
         * ⚠️ Read this before expecting it to lower memory: it does not, and
         * measurement says so. This module runs ORT on the MAIN THREAD
         * (`ort.InferenceSession` at :356, no worker), and main-thread wasm
         * memory is a one-way ratchet — measured 2026-08-31, OS working set:
         *
         *   page loaded                     309 MB
         *   +256 MB wasm on main thread     549 MB
         *   dropped every ref + forced gc   551 MB   <- NOT returned
         *   +256 MB wasm inside a Worker    806 MB
         *   worker.terminate()              551 MB   <- fully returned
         *
         * So release() hands the tensors back to ORT's allocator inside a heap
         * that never shrinks. Confirmed end-to-end: adding this call to /redub
         * moved peak RSS 3053 -> 3098 MB and retained 2007 -> 2018 MB on a 64 s
         * file, i.e. nothing, twice.
         *
         * What it IS good for: the next split reuses the arena instead of
         * growing the heap again. What would actually return the gigabyte is
         * moving this module into a Worker and terminating it — which is what
         * whisper-worker.js and supertonic-worker.js already do, and why THEIR
         * memory does come back.
         *
         * Callers that batch (vocal-remover) should not call this at all: warm
         * is exactly what they want. */
        free: function(){
            // The worker lane: terminate() — the one call here that DOES return the
            // memory (measured: see the head of this file).
            laneClose();
            try { if (onnxSession && onnxSession.release) onnxSession.release(); } catch (_) {}
            onnxSession = null; modelLoadPromise = null; modelLoadedSize = 0;
        }
    };
}

// Default Kim engine. primary:'vocal' — Kim_Vocal_2's net output IS the vocals
// (UVR registry primary_stem=Vocals; measured voiceband 0.946 on prod bytes
// 2026-07-23). .vocal = pred, .instrumental = mix − pred.
//
// ⚠️ Числа ниже — НЕ настройки, а паспорт чекпойнта: они обязаны совпадать с
// записью модели в реестре UVR (mdx_model_data.json: mdx_n_fft_scale_set /
// mdx_dim_f_set / 2**mdx_dim_t_set / compensate). Подобрать их «на слух»
// нельзя — неверный n_fft не ломает звук, а тихо съедает качество, и так
// прожили почти три месяца. Сверка — tests/Unit/MdxEngineParamsTest.php.
window.MdxStemSeparator = makeEngine({
    label: 'ultra', nFft: 7680, hop: 1024, dimF: 3072, dimT: 256, compensate: 1.009,
    primary: 'vocal',
    metaName: 'vr-ultra-model-url', metaFbName: 'vr-ultra-model-url-fallback',
    staticUrls: ['/vendor/onnx/kim-vocal-2.onnx', 'https://huggingface.co/seanghay/uvr_models/resolve/main/Kim_Vocal_2.onnx']
});

// Karaoke engine — UVR_MDXNET_KARA_2: lead/backing split (is_karaoke). primary:
// 'instrumental' — its net output = instrumental + BACKING vocals (UVR model_data
// primary_stem=Instrumental; lead voiceband 0.917 via residual). Runs on the MIX
// independently of Kim; the /vocal-remover karaoke mixer derives lead = KARA.vocal,
// backing = (all vocals) − lead.
window.MdxKaraokeSeparator = makeEngine({
    label: 'kara', nFft: 5120, hop: 1024, dimF: 2048, dimT: 256, compensate: 1.065,
    primary: 'instrumental',
    metaName: 'vr-kara-model-url', metaFbName: 'vr-kara-model-url-fallback',
    staticUrls: ['/vendor/onnx/uvr-mdxnet-kara-2.onnx', 'https://huggingface.co/seanghay/uvr_models/resolve/main/UVR_MDXNET_KARA_2.onnx']
});

// Crowd engine — UVR-MDX-NET_Crowd_HQ_1: the AUDIENCE out of a live recording
// (applause, cheering, whistles, the hall's chatter). primary:'instrumental' —
// the net's output is the recording WITHOUT the crowd (UVR model_data hash
// b02be2d1…: primary_stem «No Crowd», compensate 1.02); .vocal is the crowd
// itself (mix − pred·comp). Measured 2026-10-14 against real audience sound
// from live recordings mixed into studio songs and clean voices — see
// docs/denoise-crowd.md for where it helps and where it eats stage speech.
window.MdxCrowdSeparator = makeEngine({
    label: 'crowd', nFft: 5120, hop: 1024, dimF: 2560, dimT: 256, compensate: 1.02,
    primary: 'instrumental',
    metaName: 'crowd-model-url', metaFbName: 'crowd-model-url-fallback',
    staticUrls: ['/vendor/onnx/uvr-mdxnet-crowd-hq-1.onnx']
});

// ── Политика «включать ли Ультра по умолчанию» — ОДНА на все страницы ────
//
// До 18.08 каждая страница решала сама и одинаково неверно: «WebGPU-
// устройство создаётся → Ультра быстрая». Замер 15–18.08 это опроверг:
// устройство есть у 78% телефонов, а завершаемость авто-Ультры на мобильных
// 12.7% против фона 49.3% у Быстрого (десктоп: 59.0% против 58.3% — там
// решение было верным). Устройство ≠ скорость — тот же урок, что
// «адаптер ≠ устройство», уровнем выше.
//
// Порядок правил (первое сработавшее решает):
//   0. WebAssembly SIMD не валидируется (процессор без SSE4.1: Core 2, Phenom,
//      старые Atom) → нет, и это НЕ «медленно», а «невозможно»: ORT-web 1.24
//      поставляет только SIMD-сборку, модель здесь не запустится ни на чём.
//      До 23.09 такие машины получали `no-webgpu-device`, то есть «можно, но
//      долго», и страница предлагала им Ультру с кнопкой «Повторить» и текстом
//      про сеть или CDN (пользователь 7201: «пробовал раз 100»; RU 30 д — 385
//      сессий /vocal-remover без SIMD, 114 из них упёрлись в этот гейт).
//   1. Ультра падала в этой сессии → нет (предохранитель из split-обёртки).
//   2. Есть ЗАМЕРЕННЫЙ темп этого устройства (пишется на каждом успешном
//      прогоне): ≤1.2×RT → да на любом классе устройства; >2.5×RT → нет
//      даже на десктопе. Замер сильнее класса в обе стороны.
//   3. WebGPU-УСТРОЙСТВО не создаётся → нет (wasm в 1 поток = 9.6×RT).
//   4. Мобильное устройство без замера → нет: единственный полный мобильный
//      прогон в проде занял 18 минут на 3-минутную песню (rate 6.1).
//   5. Десктоп с устройством → да (замерено 0.38×RT, 25-кратный выигрыш).
(function(){
    // Only the GPU probe is memoised — it is the one expensive, stable part.
    // The verdict itself is re-read on EVERY call: until 27.09.2026 the whole
    // answer was cached for the page's life, so rule 1 below («Ultra failed in
    // this session») could never fire on the page where it failed — a person
    // who re-picked the file after Kim timed out waited the two minutes again
    // and failed again (prod 25.09, session 4ca21be1…: three times in a row).
    var _devicePromise = null;
    function gpuDevice(){
        if (_devicePromise) return _devicePromise;
        _devicePromise = (async function(){
            // A WebGPU device only counts if OUR build will use it: where the
            // owner hands out the plain wasm build (iPhone, WebKit without a
            // GPU) Kim runs on the CPU whatever navigator.gpu says.
            try {
                if (!ortWasmOnly() && 'gpu' in navigator) {
                    var a = await navigator.gpu.requestAdapter();
                    if (a) { var d = await a.requestDevice(); return !!d; }
                }
            } catch (_) {}
            return false;
        })();
        return _devicePromise;
    }
    function defaultEngineVerdict(){
        return (async function(){
            var out = { ultra: false, reason: '', rate: null, device: false };
            if (!wasmSimdSupported()) { out.reason = 'no-simd'; out.impossible = true; return out; }
            // The measured pace travels with EVERY answer, a refusal included: the page prices
            // what it offers by it («split off the backing vocals · ~N min»). After a hang it is
            // the pace the watchdog wrote; read only on the «yes» branch, the page priced the offer
            // by the desktop table — «~1.5 min» on a card that had just needed 20 s per second.
            try {
                var r = parseFloat(localStorage.getItem(rateKey('ultra')));
                if (isFinite(r) && r > 0) out.rate = r;
            } catch (_) {}
            try {
                var failed = sessionStorage.getItem('mdx_split_failed_ultra');
                if (failed) { out.reason = 'failed-this-session'; return out; }
            } catch (_) {}
            // The worker lane learned that this device's GPU gives up on Kim — a
            // hang, a lost device, a failed allocation. Here the model is the
            // CPU's work, and the whole recording is not its job.
            try { if (window.MdxStemSeparator.gpuGaveUpHere()) { out.reason = 'gpu-gave-up'; return out; } } catch (_) {}
            out.device = await gpuDevice();
            if (out.rate !== null) {
                if (out.rate <= 1.2) { out.ultra = true;  out.reason = 'measured-fast'; return out; }
                if (out.rate > 2.5)  { out.ultra = false; out.reason = 'measured-slow'; return out; }
            }
            if (!out.device) { out.reason = 'no-webgpu-device'; return out; }
            if (/Mobi|Android|iPhone|iPad/i.test(navigator.userAgent)) {
                out.reason = 'mobile-unproven'; return out;
            }
            out.ultra = true; out.reason = 'desktop-webgpu';
            return out;
        })();
    }
    window.MdxStemSeparator.defaultEngineVerdict = defaultEngineVerdict;
    window.MdxStemSeparator.storedRate = function(){
        try { var v = parseFloat(localStorage.getItem(rateKey('ultra'))); return (isFinite(v) && v > 0) ? v : null; }
        catch (_) { return null; }
    };
})();

// ── The worker's half of the worker lane ────────────────────────────────────
//   IN  { type:'boot', label, slug, modelUrls, providers:null|['wasm'], ort:{primary,fallback,wasmBase,wasmOnly,legacy} }
//   OUT { type:'progress', phase:'download', frac }                — bytes of the model
//   OUT { type:'ready', provider, bytes }
//   IN  { type:'predict', L, R, n }                                — 44.1 kHz, transferred
//   OUT { type:'progress', frac, info }                            — one per chunk
//   OUT { type:'result', L, R, chunks }                            — the primary stem, transferred
//   OUT { type:'gpu_lost', reason } · { type:'tele', … } · { type:'error', code, name, message, provider }
// The page ends this worker with terminate(); there is no «cancel» to ask for.
if (IN_WORKER) (function(){
    // Dependencies arrive VERSIONED in this worker's own URL (`?d=url1|url2`, built
    // by app('asset-router')->workerWithDeps) — see WorkerImportScriptsVersionedTest.
    var depUrls = (function(){
        try { var d = new URLSearchParams(self.location.search).get('d'); return d ? d.split('|').filter(Boolean) : []; }
        catch (_) { return []; }
    })();
    function importVersioned(name){
        for (var i = 0; i < depUrls.length; i++) {
            if (depUrls[i].split('?')[0].split('/').pop() === name) { importScripts(depUrls[i]); return true; }
        }
        return false;
    }
    // every makeEngine() instance by its label — a page asking the worker for an engine it
    // cannot name gets «unknown engine» (crowd, 15.10.2026: caught by the speed stand)
    var engines = { ultra: window.MdxStemSeparator, kara: window.MdxKaraokeSeparator, crowd: window.MdxCrowdSeparator };
    var eng = null, slug = 'mdx-stem-separator';
    function post(m, transfer){ self.postMessage(m, transfer || []); }
    function fail(e){
        post({ type: 'error', code: (e && e.code) || '', name: (e && e.name) || '',
               message: String((e && e.message) || e).slice(0, 300), provider: (eng && eng._serve.provider()) || '' });
    }
    self.onmessage = async function(ev){
        var m = ev.data || {};
        try {
            if (m.type === 'boot') {
                _wk = { build: ortBuildFrom(m.ort || {}), modelUrls: m.modelUrls || [], providers: m.providers || null, post: post };
                slug = m.slug || slug;
                // the fast FFT is optional: without it the exact slow path runs
                try { importVersioned('dsp-fft-mixed.js'); } catch (_) {}
                eng = engines[m.label];
                if (!eng) throw new Error('mdx worker: unknown engine ' + m.label);
                await eng._serve.ensure(slug, function(p){ post({ type: 'progress', phase: 'download', frac: p }); });
                post({ type: 'ready', provider: eng._serve.provider(), bytes: eng._serve.bytes() });
            } else if (m.type === 'predict') {
                var r = await eng._serve.predict(m.L, m.R, m.n, { slug: slug, noWatchdog: true,
                    onProgress: function(f, stage, info){ post({ type: 'progress', frac: f, info: info || null }); } });
                post({ type: 'result', L: r.L, R: r.R, chunks: r.chunks }, [r.L.buffer, r.R.buffer]);
            }
        } catch (e) { fail(e); }
    };
})();

})();
